/* ============================================
   EMAIL TEMPLATES — THE KERNEL
   Asset: email-tpl-core.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19)

   The shared machinery behind every composer email template: the data reads, the
   derivations, the availability gates, and the registry the picker talks to. It
   contains NO customer-facing copy. Copy lives in email-tpl-blocks.js (reusable
   sentences) and in the per-family template files.

   ⛔ WHY A FOLDER-SHAPED SET OF FILES AND NOT ONE BIG ONE. Ryan: "be building all this
   stuff out to be able to accept more and more emails for more and more situations with
   time ... reusable components/snippets/insertions/logic is key." A template should be a
   small file that reads like the email it sends. The tenth one has to be cheap.

   ⚠️ SHOPIFY ASSETS ARE A FLAT DIRECTORY. There is no assets/email-templates/ to put
   these in - the Asset API key is literally `assets/<name>`, and nesting is rejected. The
   `email-tpl-` prefix is the folder: every file in this system sorts together in the theme
   editor and in `ls assets/`. The human-facing folder (harness, README, how to add a
   template) is `tools/email-templates/` in the repo, which Shopify never sees.

   THE FILES, AND THE ORDER THEY MUST LOAD IN
     1. email-tpl-core.js .......... this file. Toolkit + registry. Defines window.RQDTpl.
     2. email-tpl-blocks.js ........ reusable COPY blocks built on the toolkit.
     3. email-tpl-*.js ............. one file per family, each calling RQDTpl.register().
   The sections load them with `defer`, which preserves document order, so a template file
   can assume the kernel and the blocks exist.

   TO ADD A TEMPLATE: write a build() in the right family file (or a new email-tpl-x.js
   added to both sections), call RQDTpl.register({...}). Nothing here changes. See
   tools/email-templates/README.md.

   THE CONTRACT THE MODAL SEES (unchanged, and deliberately so)
   window.RQDTemplates.list                      → [{id, label, icon, hint}]
   window.RQDTemplates.listFor(ctx)              → Promise<[{…, disabled, disabledReason}]>
   window.RQDTemplates.markSent(ref, id)         → close a once-only row in this page
   window.RQDTemplates.build(id, ctx)            → Promise<{paragraphs, warnings, attachFromMessage?}>
     ctx = { ref, attachments, toEmails }
     paragraphs = [ [{t:'text'}, {t:'linked text', link:'https://…'}],   ← a paragraph
                    {list:'bullet', runs:[{t:'…', link:'…'}]},           ← one bullet line
                    … ]
     attachFromMessage = {ref, messageId} → the modal re-attaches everything that message
                                            carried. A template never touches attachments
                                            itself; it names the send and the modal fetches.

   The modal owns insertion (it holds the Quill instance); this file never touches the DOM.
   Runs are the unit because one sentence carries a product hyperlink, and the link has to
   ride as an inline FORMAT on those characters — the same constraint the "@" picker is
   built around. No HTML crosses this boundary.

   ⛔ NO EM DASHES IN COPY *WE* WRITE. Everything a template emits is customer-facing, and
   a dash Claude reached for is the tell of a machine writing the sentence.
   ⚠️ RYAN'S OWN COPY IS THE EXCEPTION, AND IT IS VERBATIM. When he hands over a written
   email, an em dash in it is a decision: *"I don't want YOU to write them, but when I write
   them and provide it in copy, it's intentional"* (2026-08-20). Reproduce his punctuation
   exactly; do not tidy it toward the rule. The rule governs the words we invent.
   ============================================ */
(function () {
  'use strict';

  /* See `T.now()` below. 0 means "use the real clock". */
  var _nowOverride = 0;

  var STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
    MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
    NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
    ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
    TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'Washington, D.C.',
    PR: 'Puerto Rico', VI: 'the U.S. Virgin Islands', GU: 'Guam', AS: 'American Samoa',
    AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'the Northwest Territories',
    NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
    SK: 'Saskatchewan', YT: 'the Yukon',
  };

  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ============================================
     Data access
     ============================================ */
  function _api() {
    return (window.RepPortal && window.RepPortal.API) ? window.RepPortal.API : null;
  }

  /* /quote-details is the only fetch a template needs: it carries the line items
     (with product handles), the shipping address, the customer, AND workstation_meta
     — which is where the fee STATUS lives. Memoised per ref for the life of the page
     because a rep re-opening the picker on the same deal should not re-hit Shopify. */
  var _detailsCache = {};
  function _loadDetails(ref) {
    if (!ref) return Promise.resolve(null);
    if (_detailsCache[ref]) return _detailsCache[ref];
    var api = _api();
    if (!api) return Promise.resolve(null);
    _detailsCache[ref] = api.get('/quote-details?ref=' + encodeURIComponent(ref))
      .then(function (res) {
        if (!res || !res.success || !res.quote) return null;
        /* leadContext rides along for the pre-quote templates: on a deal that has not
           been built yet the ONLY record of what the customer asked about is the lead
           (sample selections carry a handle; the form answers do not). */
        return {
          quote: res.quote,
          workstationMeta: res.workstationMeta || null,
          leadContext: res.leadContext || null,
        };
      })
      .catch(function () {
        // A failed read must not cache as "this deal has nothing on it".
        _detailsCache[ref] = null;
        return null;
      });
    return _detailsCache[ref];
  }

  /* The product-mention index, borrowed from the composer so both features share one
     fetch and one definition of "a product we are allowed to link to" (unpublished
     products never enter that index, so a template can no more mail a 404 than an
     "@" mention can). Falls back to its own call if the modal file is not present. */
  function _loadMentionIndex() {
    if (window.RQD && typeof window.RQD.loadMentionIndex === 'function') {
      return window.RQD.loadMentionIndex();
    }
    var api = _api();
    if (!api) return Promise.resolve([]);
    return api.get('/product-mention-index')
      .then(function (d) { return (d && Array.isArray(d.products)) ? d.products : []; })
      .catch(function () { return []; });
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — THE ECORE RESOURCES PAGE
     ============================================
     The composer offers "To see the installation instructions for the ECORE Performance dBTile,
     click here", linking to /pages/ecore-resources with that manual's EXACT title in ?q= so the
     customer lands filtered to one document and can still browse the other 61. That last clause is
     why this is a LINK and not an attached PDF: a PDF is a dead end.

     ⛔ IT READS THE PAGE, NOT A COPY OF THE PAGE. The documents are theme-CUSTOMIZER blocks, so
     Ryan adds one with no deploy — any list of manuals baked into this file would rot the first
     time he did. The page emits a JSON island (`#bsf-ecore-doc-index`, see
     sections/custom-ecore-spec-library.liquid) carrying title / category / url / covers for all 62.

     ⚠️ SAME ORIGIN, so no worker and no CORS: the Deal Desk is /pages/rep-quote-dashboard and the
     library is /pages/ecore-resources, both Shopify pages on this storefront.

     ⛔ `window.fetch`, NEVER a bare `fetch`, and no `URL` / `URLSearchParams` anywhere in here —
     probe.mjs runs these assets inside a `vm` context that has `encodeURIComponent` and neither of
     those, so a bare identifier is a ReferenceError that takes the whole harness down.

     ⛔ REGEX + JSON.parse, not DOMParser: one code path that works in the browser AND in the
     harness, and no third-party HTML ever reaches a parser that could execute it. */
  var _ECORE_DOCS_PATH = '/pages/ecore-resources';
  var _ECORE_DOCS_URL = 'https://www.blueskyfitsupply.com/pages/ecore-resources';
  var _ECORE_ISLAND_RX = /<script[^>]*id="bsf-ecore-doc-index"[^>]*>([\s\S]*?)<\/script>/i;
  var _ecoreDocsP = null;   // the memoised fetch
  var _ecoreDocs = null;    // the parsed index, or null for UNKNOWN

  /* The resources page's own `squash`, lower-cased first. Same rule the customer's landing page
     matches on, so `dbtile`, `egrip` and `class1` behave here exactly as they do there. */
  function _ecoreSq(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ''); }

  function _parseEcoreIndex(html) {
    var m = _ECORE_ISLAND_RX.exec(String(html || ''));
    if (!m) return null;
    var data;
    try { data = JSON.parse(m[1]); } catch (_) { return null; }
    var docs = (data && Array.isArray(data.docs)) ? data.docs : null;
    if (!docs) return null;
    var manuals = docs.filter(function (d) { return d && d.cat === 'tech-manual' && d.title && d.url; });
    return { v: data.v || 1, docs: docs, manuals: manuals };
  }

  /**
   * The index, memoised for the page load. Resolves to the index or to NULL.
   * ⛔ NULL MEANS UNKNOWN AND `{manuals: []}` MEANS KNOWN-EMPTY, and the two drive different
   * sentences to the rep — "could not load the document list" versus "the resources page has no
   * tech manuals on it". Collapsing them is how a failed read gets reported as an empty shelf.
   * ⚠️ A FAILED READ IS NOT MEMOISED (the `_loadMeta` rule), so re-opening the picker retries.
   */
  function _loadEcoreDocs(opts) {
    var bust = opts && opts.fresh;
    if (_ecoreDocsP && !bust) return _ecoreDocsP;
    if (typeof window.fetch !== 'function') return Promise.resolve(null);
    /* ⚠️ `cache: 'no-cache'` keeps the BROWSER's HTTP cache from serving yesterday's copy. The
       Shopify edge cache is the lag that remains, which is what `fresh` (a cache-busting query) is
       for — the sub-picker's "Refresh list" is the rep-facing answer to "I added it 30 seconds
       ago". ⛔ No sessionStorage: the page is already edge-cached and a second stale layer would
       produce "I added a manual, hard-refreshed, and still cannot see it". */
    var url = _ECORE_DOCS_PATH + (bust ? '?_=' + Date.now() : '');
    var p = window.fetch(url, { cache: 'no-cache', credentials: 'same-origin' })
      .then(function (r) {
        if (!r || !r.ok) throw new Error('HTTP ' + (r && r.status));
        return r.text();
      })
      .then(function (html) {
        var idx = _parseEcoreIndex(html);
        if (!idx) throw new Error('the document index is not on that page');
        _ecoreDocs = idx;
        return idx;
      })
      .catch(function () {
        _ecoreDocsP = null;   // unknown, and worth asking again
        return null;
      });
    _ecoreDocsP = p;
    return p;
  }

  function _refreshEcoreDocs() { _ecoreDocsP = null; _ecoreDocs = null; return _loadEcoreDocs({ fresh: true }); }

  /** A sync peek — the loaded index or null. NEVER fetches, so `available()` may call it. */
  function _ecoreDocsNow() { return _ecoreDocs; }

  /**
   * The customer-facing deep link. ⛔ ABSOLUTE (a relative href in an email is a dead link), the
   * `www` host to match the canonical, the EXACT block title, and no `&all=1`.
   * ⛔ The em dash MUST stay percent-encoded: a raw one in an href is what Outlook and Quill
   * mangle, and probe.mjs's no-em-dash check would go red on it, correctly.
   */
  function _ecoreManualUrl(title) {
    return _ECORE_DOCS_URL + '?q=' + encodeURIComponent(String(title == null ? '' : title));
  }

  /** What a manual is called when no quote named the product: `short`, else the title's first half. */
  function _ecoreManualName(doc) {
    var short = String((doc && doc.short) || '').trim();
    if (short) return short;
    var t = String((doc && doc.title) || '').trim();
    var cut = t.split(/\s+[—–-]\s+/)[0].trim();
    return (cut || t).replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  /** "ECORE Performance dBTile" — the no-quote wording, without doubling the brand. */
  function _ecoreBrandedName(doc) {
    var n = _ecoreManualName(doc);
    return /^ecore\b/i.test(n) ? n : 'ECORE ' + n;
  }

  /* ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — HOW A COVERS ENTRY MATCHES A QUOTE LINE
     ============================================
     ⛔ EVERY WORD MUST APPEAR; THE ORDER DOES NOT. The first cut looked for the covers entry as a
     contiguous run of text inside the product title, and when Ryan filled in all 20 manuals
     EIGHTEEN of his entries matched nothing — because he writes products the way a rep says them
     and Shopify names them differently:

       "8mm Basic Fit Rubber Roll"   vs   "ECORE Basic Fit Rubber Roll - 8mm Flooring"
       "Bounce 2 Vinyl Roll"         vs   "ECORE Bounce 2 Motivate Laminate Roll - 7mm Class 1"
       "RageTurf Interlocking Tiles" vs   "ECORE RageTurf Rally Interlocking Tile - 12mm ..."

     Same words, reordered or with a word pushed in between. Asking a human to transcribe forty
     merchandising titles by hand is a worse answer than accepting the way they already write, and
     it would rot the next time a title changed. This is also the rule the RESOURCES PAGE'S OWN
     SEARCH BOX uses (tokenize, AND across tokens), so a rep who has used one has learned the other.

     ⚠️ SINGULAR AND PLURAL AGREE. "tiles" must match "tile" and "paver" must match "pavers", so a
     token matches when the shorter of the two is a PREFIX of the longer — but only when the
     shorter is at least three characters. Below that the prefix rule is noise: it would let "1"
     match "12mm", and a stray digit in a covers entry would attach a manual to half the catalogue.
     Short tokens must be exactly equal, which is what keeps `Rubber Paver 1"` off the 1-3/4" paver
     while `Rubber Paver 1-3/4"` still lands on it.

     ⚠️ THE SCORE IS STILL THE SQUASHED LENGTH, so longest-match keeps working across the new rule:
     "Performance Beast Plus" outranks "Performance Beast" on the same line. */
  function _ecoreTokens(s) {
    return String(s == null ? '' : s).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  function _ecoreTokenHit(t, h) {
    if (t === h) return true;
    var shorter = t.length < h.length ? t : h;
    var longer = t.length < h.length ? h : t;
    return shorter.length >= 3 && longer.indexOf(shorter) === 0;
  }

  function _ecoreEntryMatches(entryToks, hayToks) {
    if (!entryToks.length) return false;
    return entryToks.every(function (t) {
      return hayToks.some(function (h) { return _ecoreTokenHit(t, h); });
    });
  }

  /**
   * Does this quote line name something a customer actually LAYS?
   * ⚠️ Matched to the index by HANDLE first, then by exact title — the same two-step
   * `_inquiryProducts` uses, because a Workstation line can carry a title and no handle.
   */
  /* CUSTOM_ROLLSENTENCE_BY_CLAUDE (Ryan 2026-09-24) — the line's `unit:` tag, as the mention index
     carries it (`sqft` for a roll, `tile`, `paver`, `plank`), or '' when the line is not in the
     index. Split out of _lineHasFloorUom because the layout email needs the VALUE: "minimum
     required roll lengths" is a sentence about rolls, and a plank quote must not carry it. */
  function _lineUom(li, index) {
    var idx = Array.isArray(index) ? index : [];
    if (!idx.length) return '';
    var h = String((li && li.handle) || '').toLowerCase();
    var entry = h ? idx.find(function (pr) { return pr && String(pr.h || '').toLowerCase() === h; }) : null;
    if (!entry) {
      var tl = String((li && li.title) || '').toLowerCase().trim();
      entry = tl ? idx.find(function (pr) { return pr && String(pr.t || '').toLowerCase().trim() === tl; }) : null;
    }
    return entry ? String(entry.uom || '').trim() : '';
  }
  function _lineHasFloorUom(li, index) {
    return !!_lineUom(li, index);
  }

  function _ecoreCoversList(doc) {
    return String((doc && doc.covers) || '').split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /**
   * Quote lines -> the manuals that govern them.
   * Returns [{doc, name, line, value}], best match per line, biggest line first.
   *
   * ⛔ LONGEST MATCH WINS, and that is correctness rather than polish: squashing destroys word
   * boundaries, so `performancebeast` is a substring of `performancebeastplus`. A Beast Plus line
   * matches both entries and only the longer one names the right product.
   *
   * ⛔ A NON-BLANK, NON-ECORE VENDOR IS EXCLUDED OUTRIGHT — a Legend rack must never pull a
   * rubber-flooring manual, whatever somebody typed into a covers list.
   *
   * ⚠️ MATCHED AGAINST THE TITLE **AND THE HANDLE** (Ryan 2026-09-13). A quote line carries both,
   * and a handle is the more stable of the two: `ECORE Rubber Floor Tiles - 8mm Interlocking` says
   * nothing about the Fit Collection while its handle
   * (`ecore-athletic-fit-collection-8mm-rubber-interlocking-tile`) says it plainly. Title-only
   * matching would have missed that product and nobody would have noticed.
   */
  /**
   * ⛔ `opts.floorOnly` KEEPS ONLY ACTUAL FLOOR COVERINGS (Ryan 2026-09-13, asked three times).
   * A quote carrying RageTurf dBTile, Quad Blok connectors and a pail of E-Grip III produced
   * "…here for the RageTurf dBTile, here for the Quad Blok, and here for the E-Grip III". The
   * accessories do have manuals; the email is reserved for what somebody is actually laying.
   *
   * THE SIGNAL IS THE `unit:` TAG, which merchandising already sets and which the product-mention
   * index already carries as `uom`. Every roll, tile, paver and plank has one (sqft / tile / paver
   * / plank); the adhesive, the cleaner, the stripper, the protectant, the Quad Blok connectors,
   * the weld rods, the edge reducers and the free samples have none. Verified across all 57 live
   * ECORE products, not inferred from a handful.
   *
   * ⛔ NOT A LIST OF ACCESSORY NAMES, and not a checkbox Ryan has to remember to untick. Either
   * would put his rule in his own maintenance queue, and the first accessory added after the day
   * somebody forgets is back in a customer's email.
   *
   * ⚠️ IT FAILS QUIET. A line the index does not know (unpublished, or a hand-typed custom line)
   * has no uom and is skipped, and an index that failed to load skips everything — so the sentence
   * disappears rather than naming something it cannot justify. For a line the email writes BY
   * ITSELF that is the right direction; a rep who wants the manual anyway still has the snippet.
   */
  function _ecoreManualsForQuote(lineItems, manuals, opts) {
    var list = Array.isArray(manuals) ? manuals : [];
    var out = [];
    if (!list.length) return out;
    var floorOnly = !!(opts && opts.floorOnly);
    var index = (opts && Array.isArray(opts.index)) ? opts.index : [];
    (Array.isArray(lineItems) ? lineItems : []).forEach(function (li) {
      if (!li || li.isSpacer) return;
      var title = String(li.title || '').trim();
      if (!title) return;
      var vendor = String(li.vendor || '').trim();
      if (vendor && !_isEcore(vendor) && !/^ecore\b/i.test(title)) return;
      if (floorOnly && !_lineHasFloorUom(li, index)) return;

      var hayToks = _ecoreTokens(title).concat(_ecoreTokens(li.handle || ''));
      var haySq = _ecoreSq(title) + ' ' + _ecoreSq(li.handle || '');
      var best = null;
      list.forEach(function (doc) {
        _ecoreCoversList(doc).forEach(function (entry) {
          var sq = _ecoreSq(entry);
          /* ⚠️ A very short entry is refused outright. One or two characters cannot identify a
             product and would attach a manual to anything; an entry that short is a typo. */
          if (sq.length < 4) return;
          if (!_ecoreEntryMatches(_ecoreTokens(entry), hayToks)) return;
          /* ⛔ A CONTIGUOUS MATCH OUTRANKS A SCATTERED ONE, WHATEVER THE LENGTHS. Order-independent
             matching is what makes a rep's own phrasing work, and it is also how a stray
             marketing word wins an argument it should lose: "ECORE Heritage Motivate Vinyl Plank
             - 7mm PVT PERFORMANCE Flooring" contains both words of "Performance Motivate", so on
             length alone (19 vs 16) the Performance Rolls manual beat the Heritage one on the
             Heritage product. "Heritage Motivate" is a real phrase in that title; "Performance
             Motivate" is two words that happen to both be present. Found replaying the shipped
             matcher over Ryan's own Covers data, 2026-09-13. */
          var contiguous = haySq.indexOf(sq) !== -1 ? 1 : 0;
          if (!best || contiguous > best.contig || (contiguous === best.contig && sq.length > best.len)) {
            best = { doc: doc, entry: entry, len: sq.length, contig: contiguous };
          }
        });
      });
      if (!best) return;
      var qty = parseInt(li.quantity, 10) || 1;
      var unit = parseFloat(li.directPrice || li.price || 0) || 0;
      out.push({
        doc: best.doc,
        /* ⛔ THE NAME IS THE COVERS ENTRY AS RYAN TYPED IT, never T.displayName(li.title): that
           helper cuts at the first spaced hyphen and yields "ECORE Performance dBTile Rubber
           Tile", not "Performance dBTile". This is the single most important line in the file. */
        name: String(best.entry).replace(/^ecore\s+/i, '').trim(),
        line: li,
        value: qty * unit,
      });
    });
    out.sort(function (a, b) { return b.value - a.value; });
    return out;
  }

  /* Through the modal's union, not RPDash directly: the Quote Workstation never loads
     dashboard-main.js, so `RPDash.allQuotes` is undefined on that page and this used to
     answer null there - which silently dropped the "thank you for your time on the phone"
     line on every template built from the Workstation. */
  function _dealByRef(ref) {
    var all = (window.RQD && typeof window.RQD.knownDeals === 'function')
      ? window.RQD.knownDeals(ref)
      : ((window.RPDash && Array.isArray(window.RPDash.allQuotes)) ? window.RPDash.allQuotes : []);
    return (all || []).find(function (x) { return x && x.reference === ref; }) || null;
  }

  /* The chain lookup backs both the deal resolution above and the attach menu; awaiting it
     here means a template never renders from a half-loaded picture of the deal. */
  function _ensureChain(ref) {
    if (window.RQD && typeof window.RQD.loadChainDeals === 'function') {
      return window.RQD.loadChainDeals(ref).catch(function () { return []; });
    }
    return Promise.resolve([]);
  }

  /* ============================================
     Derivations
     ============================================ */
  /* ============================================
     A name, spelled properly
     ============================================
     ⛔ MIXED CASE IS NOT EVIDENCE OF INTENT. This used to leave any mixed-case segment alone
     on the theory that it was a McDonald or an O'Brien, which is how a real submission
     ("BRian wAlrod") reached a vendor subject line and a customer's own emails untouched on
     2026-08-20. Every segment is now rewritten First-upper / rest-lower unless it is a
     recognised compound or a roman-numeral suffix.

     ⚠️ THIS IS A DELIBERATE SECOND COPY of `normalizeDisplayName` in the worker
     (rfq-worker src/lib/text-normalize.js). The theme cannot import from the worker and the
     worker cannot import from the theme, and the alternative - a name spelled one way in the
     subject the composer shows and another way in the email the worker sends - is worse than
     the duplication. If one changes, change both.

     ⚠️ The trade-off is the same one made there: an intentional interior capital that is not
     a listed compound ("JoAnn") flattens to "Joann". Careless typing is many times more
     common, and a compound can be added to the list. */
  var _COMPOUND_PREFIX = /^(Mc|Mac|Fitz|De|Di|Du|Da|Le|La|Van|Von|Del|Des|Dos|St)([A-Za-z].*)$/;
  var _ROMAN_SUFFIX = /^(?:I{1,3}|IV|VI{0,3}|IX|XI{0,2})$/;

  function _properName(name) {
    if (!name) return '';
    return String(name).trim().split(/(\s|-|')/).map(function (part) {
      if (!/[A-Za-z]/.test(part)) return part;
      if (_ROMAN_SUFFIX.test(part)) return part;              // "III" must not become "Iii"
      var canon = part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      if (part === canon) return part;
      /* A compound counts only when the typist SIGNALLED one with an interior capital, so
         "Delores" (no interior capital) is left to the canonical path and stays itself. */
      var m = part.match(_COMPOUND_PREFIX);
      if (m && /[A-Z]/.test(m[2].charAt(0))) {
        return m[1] + m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase();
      }
      return canon;
    }).join('');
  }

  function _firstName(full) {
    var s = String(full || '').trim();
    if (!s) return '';
    if (s.indexOf('@') >= 0 && s.indexOf(' ') === -1) return '';   // an email address is not a name
    var first = s.split(/\s+/)[0];
    /* "MR." / "DR" style honorifics would greet the customer as "Hi Mr,". */
    if (/^(mr|mrs|ms|dr|coach|prof)\.?$/i.test(first)) {
      var rest = s.split(/\s+/)[1];
      first = rest || '';
    }
    if (!first) return '';
    /* ⚠️ The casing rule lives in _properName now, one implementation for the greeting and
       for the full name a subject line prints. This used to carry its own copy of it and
       that copy had the mixed-case hole. */
    return _properName(first).replace(/[^A-Za-z'\-]/g, '');
  }

  /* One definition, owned by dashboard-email-modal.js, which holds both insertion paths
     (this template and the "@" picker). Local fallback only so this file still works if it
     is ever loaded on its own. See the note over _productDisplayName there. */
  function _displayName(title) {
    if (window.RQD && typeof window.RQD.productDisplayName === 'function') {
      return window.RQD.productDisplayName(title);
    }
    var t = String(title == null ? '' : title);
    var m = t.match(/\s[-\u2013\u2014]\s/);
    return (m ? t.slice(0, m.index) : t).trim() || t.trim();
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — the flooring colour as a CUSTOMER reads it.

     The worker publishes `line.color` raw, code and all, because the supplier and the PO need
     the code (see lib/flooring-color.js). A customer does not. Ryan: *"a lot of these colors
     will be something like 'Grippin Gray 10 - EL02'. All we need is everything up to the number,
     so in that case, just 'Grippin Gray'. Or if the color was just 'Grippin Gray - EL02' then
     we'd still write 'in the grippin gray color.'"*

     Two cuts, in this order, because a value can carry either half or both:
       "Grippin Gray 10 - EL02"  -> cut at the separator -> "Grippin Gray 10" -> "Grippin Gray"
       "Grippin Gray - EL02"     -> "Grippin Gray"
       "Crema - 1500"            -> "Crema"
       "Buff Blue/Gray - EL04"   -> "Buff Blue/Gray"   (the slash is not a separator)
       "Black Grass"             -> "Black Grass"      (neither half present, left alone)

     ⚠️ Whitespace is collapsed FIRST. "Raven  - 300" is a real catalog value with a double space,
     and the separator pattern wants single spaces around the dash.
     ⚠️ The trailing-number cut is capped at 3 digits so it can only ever eat a gauge marker
     (10 / 20 / 2). Verified against all 183 live ECORE colour values — every value ending in a
     bare number is a gauge, and no colour NAME ends in one. */
  function _floorColorLabel(raw) {
    var s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
    if (!s) return '';
    var cut = s.split(/\s[-–—]\s/)[0].trim().replace(/\s\d{1,3}$/, '').trim();
    return cut || s;
  }

  function _stateName(code) {
    var c = String(code || '').trim().toUpperCase();
    return STATE_NAMES[c] || '';
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — where the freight is going, as "City, ST".

     ⛔ IT IS NOT NECESSARILY THE READER'S OWN LOCATION. The freight sentence used to say
     "shipping to your location in Michigan". Ryan: *"Sometimes, we'll sell to architects and
     construction companies and we'll actually ship to their clients locations."* On those deals
     the sentence told an architect in one city that a gym in another was theirs. Naming the
     destination is both correct for a third-party ship-to and MORE useful on a normal one — a
     buyer confirming an address wants the address, not a state.

     Ladder, each rung the most specific thing that is still true:
       city + state  -> "Grand Rapids, MI"   (Ryan's format: abbreviation, not the full name)
       city only     -> "Grand Rapids"
       state only    -> "Michigan"           (a bare "MI" reads like a typo with no city on it)
       nothing       -> ''                   (caller falls back to "your location")

     ⚠️ City runs through _properName because a Shopify address holds whatever was typed, and
     "GRAND RAPIDS" or "grand rapids" would go out in a customer email exactly as entered. Same
     reasoning as the name repair that helper already exists for. The STATE code is deliberately
     left upper-case: "MI" is an abbreviation, not a word. */
  function _shipDestination(addr) {
    if (!addr) return '';
    var city = _properName(String(addr.city || '').trim());
    var code = String(addr.provinceCode || '').trim().toUpperCase();
    if (city && code) return city + ', ' + code;
    if (city) return city;
    return _stateName(code);
  }

  /* Ryan's definition, verbatim: the TOTAL column, not the unit price. 1,000 sq ft of
     flooring at $2.00 outranks an $800 bucket of adhesive. directPrice (the rep's
     selling price) wins over the Shopify draft's MAP-stamped price, same rule the
     Order Contents card uses — otherwise the ranking is done on numbers the customer
     never sees. */
  function _mostExpensiveLine(lineItems) {
    var best = null, bestTotal = -1;
    (Array.isArray(lineItems) ? lineItems : []).forEach(function (li) {
      if (!li || li.isSpacer) return;
      var unit = parseFloat(li.directPrice);
      if (!isFinite(unit) || unit <= 0) unit = parseFloat(li.price) || 0;
      var qty = parseInt(li.quantity, 10) || 0;
      var total = unit * qty;
      if (total > bestTotal) { bestTotal = total; best = li; }
    });
    return bestTotal > 0 ? best : null;
  }

  /* Handle first (it is the product's identity and survives a title edit on the quote),
     title only as a fallback for a line whose handle never made it onto the draft. */
  /* Handle, then SKU, then exact title — the same order the worker's own resolveProductUrls
     (product-catalog.js) has always used for the quote PDF's links.

     ⛔ SKU WAS THE MISSING RUNG, AND IT IS THE ONLY ONE A WORKSTATION CUSTOM LINE HAS. Such a
     line carries no variant and no handle (its SKU rides in the `_sku` custom attribute, which
     /quote-details resolves into `line.sku`), so handle matching cannot fire — and exact title
     matching breaks the moment a rep rewords the title for the quote, which is a normal thing
     to do. RW-1342-A quoted "Basic Fit Rubber Roll - 8mm Flooring" against a live product
     titled "ECORE Basic Fit Rubber Roll - 8mm Flooring": one dropped word, and the email told
     the rep the product was "not in the product index" while the PDF beside it linked the same
     product fine, because the PDF resolver did look at the SKU.

     ⚠️ Deliberately EXACT at every rung. A fuzzy title match would link the wrong product into
     a customer's email, which is far worse than no link at all — the caller already warns the
     rep when a line comes back unlinked. */
  function _linkForLine(line, index) {
    if (!line || !Array.isArray(index) || !index.length) return '';
    var h = String(line.handle || '').toLowerCase();
    if (h) {
      var byHandle = index.find(function (p) { return p && String(p.h || '').toLowerCase() === h; });
      if (byHandle) return byHandle.u || '';
    }
    var sku = String(line.sku || '').trim().toUpperCase();
    if (sku) {
      var bySku = index.find(function (p) {
        if (!p || !Array.isArray(p.s)) return false;
        return p.s.some(function (s) { return String(s || '').trim().toUpperCase() === sku; });
      });
      if (bySku) return bySku.u || '';
    }
    var t = String(line.title || '').toLowerCase().trim();
    if (!t) return '';
    var byTitle = index.find(function (p) { return p && String(p.t || '').toLowerCase().trim() === t; });
    return byTitle ? (byTitle.u || '') : '';
  }

  /* "Thank you again for your time on the phone" is only true if they actually spoke.
     callOutcome is one of inbound | connected | no-answer (dashboard-main.js ~2990), so
     a no-answer or a voicemail must NOT produce the line. An entry with no outcome at
     all predates that selector, and the rep did log a call, so it counts. */
  function _lastSpokenCall(deals) {
    var list = Array.isArray(deals) ? deals : (deals ? [deals] : []);
    var best = null;
    list.forEach(function (q) {
      var entries = (q && Array.isArray(q.noteEntries)) ? q.noteEntries : [];
      entries.forEach(function (n) {
        if (!n || !n.ts) return;
        var types = Array.isArray(n.types) && n.types.length ? n.types : (n.type ? [n.type] : []);
        if (types.indexOf('call') === -1) return;
        if (n.callOutcome && n.callOutcome !== 'inbound' && n.callOutcome !== 'connected') return;
        if (!best || new Date(n.ts) > new Date(best.ts)) best = n;
      });
    });
    return best;
  }

  /* The last OUTBOUND ATTEMPT that went unanswered, which is the event the inquiry
     follow-up is written about. Distinct from _lastSpokenCall on purpose: that one exists
     to decide whether you may thank someone for their time, this one to describe a call
     they never picked up. `callVm` ('left' | 'none') rides on the same entry and is what
     lets the copy say "left you a voicemail" instead of "tried you by phone" — the
     activity card has rendered that distinction since 2026-05-29; the templates were the
     only reader that threw it away. */
  function _lastNoAnswerCall(deals) {
    var list = Array.isArray(deals) ? deals : (deals ? [deals] : []);
    var best = null;
    list.forEach(function (q) {
      var entries = (q && Array.isArray(q.noteEntries)) ? q.noteEntries : [];
      entries.forEach(function (n) {
        if (!n || !n.ts) return;
        var types = Array.isArray(n.types) && n.types.length ? n.types : (n.type ? [n.type] : []);
        if (types.indexOf('call') === -1) return;
        if (n.callOutcome !== 'no-answer') return;
        if (!best || new Date(n.ts) > new Date(best.ts)) best = n;
      });
    });
    return best;
  }

  /* ⛔ The call belongs to the CHAIN, not to one revision. A rep who spoke to the customer
     and then built -B is looking at a call logged on -A; scanning only the composer's own
     record dropped the thank-you line exactly when it was most likely to be true. Same
     "a revision chain is one conversation" rule the attach menu and the "@" ranking use. */
  function _chainDeals(ref) {
    var base = String(ref || '').replace(/-[A-Z]$/i, '');
    var all = (window.RQD && typeof window.RQD.knownDeals === 'function')
      ? window.RQD.knownDeals(ref)
      : ((window.RPDash && Array.isArray(window.RPDash.allQuotes)) ? window.RPDash.allQuotes : []);
    if (!base) return [];
    return (all || []).filter(function (q) {
      return q && q.reference && String(q.reference).replace(/-[A-Z]$/i, '') === base;
    });
  }

  /* ============================================
     THE HOUSE TIME PHRASE (Ryan 2026-08-19)
     ============================================
     ⛔ ONE ladder, for every date reference in every template. Ryan: "this is the logic
     we'll use for all date/time references in all emails ... finetuning exact verbiage
     should we need based on the context of the email, but keeping those general timelies."
     So a template may re-word around it, but nothing gets its own ladder.

        < 15 min ................. "just now"        (+ the `just` flag, see below)
        < 90 min ................. "a little bit ago"
        same calendar day ........ "earlier today"
        1 day .................... "yesterday"
        2 days ................... "a couple of days ago"
        3-6 days, this week ...... "on Thursday"
        3-27 days, last week ..... "last week"
        7-27 days ................ "on the 14th"  /  "on the 14th of July" (other month)
        28+ days, last month ..... "last month"
        28+ days ................. "on the 14th of July"  (+ year when the year differs)

     Calendar-day difference in the REP's own timezone, so an 11pm call is "yesterday" the
     next morning rather than "today" for another hour. A "week" runs Sunday to Saturday,
     matching WEEKDAYS above.

     RETURNS AN OBJECT, not a string, because "just now" is the one rung that does not sit
     at the end of a sentence: you write "I just left you a voicemail", not "I left you a
     voicemail just now". `just` lets the caller move the word to where English wants it,
     and every other rung ignores the flag.

     `recent` (Ryan 2026-08-26) marks the rungs down to and including "last week" — every
     phrase a customer still reads as NEARBY. Beyond it the ladder starts naming a calendar
     date ("on the 10th", "on the 31st of May 2025") or a whole month, and a pleasantry built
     on one of those stops sounding warm and starts sounding like a filing cabinet. Ryan:
     *"anything beyond 'last [fill in the blank]' means that line is disqualified… we're not
     going to email someone saying 'thanks very much for your time on the phone back in May of
     last year' haha, that sounds ridiculous."*

     ⛔ IT IS A FLAG ON THE LADDER, NOT A TEST IN THE TEMPLATE. A caller that re-derived
     "is this recent" from its own date math, or by sniffing the phrase for "on the", would be
     a second ladder — the exact thing the house rule above forbids, and it would silently
     disagree the first time a rung moved. The cut sits where the vocabulary changes: the last
     `recent` rung is the last one that names no date.

     ⚠️ `recent` is about whether to SAY the phrase, never about whether the phrase is right.
     Every rung still returns its correct wording; a caller free to mention an old date (a rep-
     facing warning, a log line) simply ignores the flag.

     `fresh` (Ryan 2026-09-15) is a SECOND, TIGHTER cut on the same ladder, and it exists for a
     different failure than `recent`. `recent` asks whether a phrase sounds warm; `fresh` asks
     whether mentioning the event at all invites the question *"why did you call me then and
     only email me now?"*. Follow-up 1 was opening with "I called and left you a voicemail on the
     26th of August to follow up on the quote I sent over" — every word of it true, and it read
     as though nobody had looked at the deal in three weeks. Ryan: *"No more than 2 days ago.
     Meaning, 3 days ago would be too much. That would make people stop and wonder 'why did you
     call me 3 days ago and only today, 3 days later, decide to email me and mention it?'"*

     So `fresh` stops at "a couple of days ago" — three rungs above where `recent` stops — and
     "on Thursday" and "last week" are `recent: true, fresh: false`. That pair is not a
     contradiction: a call last week is still a NEARBY thing to say ("thanks again for your time
     on the phone last week", once you have them) and still a stale thing to LEAD a chase with.
     The two flags answer two questions and the callers pick.

     ⛔ IT IS THE SAME HOUSE RULE: a flag on the ladder, never date math in a template. A
     caller that counted its own two days would be the second ladder the note above forbids.

     A future timestamp (clock skew, a hand-edited entry) yields '' and the caller drops
     the timing rather than telling the customer you will call them last Tuesday. */
  function _ordinal(n) {
    var v = n % 100;
    if (v >= 11 && v <= 13) return n + 'th';
    return n + ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  }
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  function _whenPhrase(ts, nowMs) {
    var none = { phrase: '', just: false, recent: false, fresh: false };
    var t = new Date(ts);
    if (isNaN(t.getTime())) return none;
    var now = new Date(nowMs);
    var mins = (now.getTime() - t.getTime()) / 60000;
    if (mins < 0) return none;                        // clock skew / a future-dated entry
    if (mins < 15) return { phrase: 'just now', just: true, recent: true, fresh: true };
    if (mins < 90) return { phrase: 'a little bit ago', just: false, recent: true, fresh: true };

    var d0 = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    var d1 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var days = Math.round((d1 - d0) / 86400000);
    if (days <= 0) return { phrase: 'earlier today', just: false, recent: true, fresh: true };
    if (days === 1) return { phrase: 'yesterday', just: false, recent: true, fresh: true };
    if (days === 2) return { phrase: 'a couple of days ago', just: false, recent: true, fresh: true };

    /* ⛔ EVERY RUNG BELOW THIS LINE IS `fresh: false` — see the `fresh` note in the header
       block. The cut is here because this is the last rung a reader would not have to date
       themselves: past "a couple of days ago" the phrase names a day, and naming a day invites
       the question the flag exists to avoid. */

    // Sunday-anchored week boundaries, so "last week" means the previous CALENDAR week
    // rather than "8 to 14 days back" — a Monday call is "last week" on Tuesday.
    var thisWeekStart = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate() - d1.getDay());
    var lastWeekStart = new Date(thisWeekStart.getFullYear(), thisWeekStart.getMonth(), thisWeekStart.getDate() - 7);
    var inLastWeek = (d0 >= lastWeekStart && d0 < thisWeekStart);
    if (days <= 6) return inLastWeek ? { phrase: 'last week', just: false, recent: true, fresh: false }
                                     : { phrase: 'on ' + WEEKDAYS[t.getDay()], just: false, recent: true, fresh: false };
    if (inLastWeek) return { phrase: 'last week', just: false, recent: true, fresh: false };

    /* ⛔ EVERY RUNG BELOW THIS LINE IS `recent: false` — this is where the ladder stops saying
       "lately" and starts naming a date. See the `recent` note in the header block. */
    var sameMonth = (t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth());
    if (days <= 27) {
      return { phrase: sameMonth ? 'on the ' + _ordinal(t.getDate())
                                 : 'on the ' + _ordinal(t.getDate()) + ' of ' + MONTHS[t.getMonth()],
               just: false, recent: false, fresh: false };
    }
    // 28+ days. "last month" only when it really is the previous calendar month, or a
    // 29-day gap that happens to straddle two months would read as "last month" in
    // mid-August for a July 31st call while a July 2nd call read the same way.
    /* ⚠️ "last month" READS like a `recent` phrase and is not one. It only ever fires at 28+
       days, which is strictly older than the "on the 10th" rung Ryan disqualified by name, so
       marking it recent would let through the very case that prompted the flag. The cut is the
       ladder's rungs in age order, not which English words happen to start with "last". */
    var lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    if (t.getFullYear() === lastMonth.getFullYear() && t.getMonth() === lastMonth.getMonth()) {
      return { phrase: 'last month', just: false, recent: false, fresh: false };
    }
    var yr = (t.getFullYear() !== now.getFullYear()) ? ' ' + t.getFullYear() : '';
    return { phrase: 'on the ' + _ordinal(t.getDate()) + ' of ' + MONTHS[t.getMonth()] + yr,
             just: false, recent: false, fresh: false };
  }

  /* String form, for the callers that only ever place the phrase at the end of a clause. */
  function _callWhenPhrase(ts, nowMs) { return _whenPhrase(ts, nowMs).phrase; }

  /* WHICH quotes are on this email, as refs. Drives quote/quotes, PDF/PDFs, and (Ryan
     2026-08-18) the item list: each attached quote contributes its own most expensive
     item, so a -A and a -B whose top lines differ both get named.

     Read from the attachment FILENAMES rather than a flag we set when adding them: every
     path that puts a quote on a composer (the QW Send-Quote pre-attach, the deal's "Quote
     PDF" row, a chain revision, a previously-sent re-attach) names the file from the same
     /pdf Content-Disposition, which always leads with the ref. So this is right no matter
     which door the file came through, including a rep who saved the PDF and dragged it
     back in. Distinct revisions, sorted, so the same quote attached twice is one. */
  function _attachedQuoteRefs(attachments, ref) {
    var base = String(ref || '').replace(/-[A-Z]$/i, '');
    if (!base) return [];
    var rx = new RegExp(base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(-[A-Z])?', 'i');
    var seen = {};
    (Array.isArray(attachments) ? attachments : []).forEach(function (a) {
      var n = String((a && a.name) || '');
      var m = n.match(rx);
      if (m) seen[String(m[0] || '').toUpperCase()] = 1;
    });
    return Object.keys(seen).sort();
  }

  /* "the A", "the A and B", "the A, B, and C" — Ryan's rule, as runs so each name can
     keep its own hyperlink. */
  function _joinRuns(items) {
    var out = [];
    items.forEach(function (it, i) {
      if (i > 0) {
        if (items.length === 2) out.push({ t: ' and ' });
        else out.push({ t: i === items.length - 1 ? ', and ' : ', ' });
      }
      out.push(it.url ? { t: it.title, link: it.url } : { t: it.title });
    });
    return out;
  }

  /* Fee state. workstation_meta.fees is the authority — it is where 'pending' / 'free' /
     'none' / 'value' actually live (workstation-totals.js reads exactly this). The
     quote's shippingAmount cannot answer the question on its own: Pending and FREE are
     BOTH a zero-priced line, so a number alone cannot tell a rep still waiting on a
     carrier from a promotion. Matched by title, not by array position. */
  /* ⛔ On the Quote Workstation, the LIVE page state outranks anything KV can tell us.
     Ryan: *"I set the freight to FREE and it said that freight was still being calculated."*
     `window.QWS.state.fees` is the very array the FREE selector writes to, and it is right
     even before a save and even while KV is still propagating that save to this POP. The
     saved record is one read-after-write behind by construction; the page is not.
     Guarded on the ref, so a stale Workstation left open on another deal cannot leak its
     fees into a template built for this one. */
  function _liveFees(ref) {
    try {
      var st = (window.QWS && window.QWS.state) ? window.QWS.state : null;
      if (!st || !Array.isArray(st.fees) || !st.fees.length) return null;
      if (!ref || !st.ref || String(st.ref) !== String(ref)) return null;
      return st.fees;
    } catch (_) { return null; }
  }

  /* ⛔ THE FEE LIVES IN `rawFees`, NOT `fees`. handleSaveWorkstation stores
         const customFees = (fees || []).filter(f =>
           f.title !== 'Shipping & Freight' && f.title !== 'Installation');
         ... fees: customFees, rawFees: fees || []
     so `wm.fees` is the CUSTOM fee list and excludes these two by construction. Reading it
     found nothing, fell through to the amount, and FREE is a zero - so a promotion read as
     "still being quoted" on every quote. Ryan: "I set the freight to FREE and it said that
     freight was still being calculated."
     📌 The shape was inferred from the Workstation's in-memory state and from the totals
     renderer, both of which DO hold all four fees. Neither is what gets persisted. */
  function _feeState(workstationMeta, title, amountFallback) {
    var raw = (workstationMeta && Array.isArray(workstationMeta.rawFees)) ? workstationMeta.rawFees : null;
    var custom = (workstationMeta && Array.isArray(workstationMeta.fees)) ? workstationMeta.fees : null;
    var fees = raw || custom;
    if (fees) {
      var f = fees.find(function (x) { return x && String(x.title || '').toLowerCase() === title.toLowerCase(); });
      if (f && f.status) {
        var _st = String(f.status);
        var _amt = parseFloat(f.amount) || 0;
        /* ⛔ A `value` WITH NO AMOUNT IS PENDING, BECAUSE THAT IS WHAT THE PDF PRINTS. Found on
           RW-1375-A (Ryan 2026-09-13): the record held
           `{title:'Shipping & Freight', amount: 0, status:'value'}`, the proposal's totals box read
           "+ Freight / Shipping   Pending", and the email said "The quote includes the cost of
           shipping to Renton, WA" — about a cost that is not on the page it is attached to.
           quote-pdf.js has always decided this from the AMOUNT, not the status:
             shippingLinePrice ? money(...) : (status === 'free' ? 'FREE' : 'Pending')
           so a zero prints Pending whatever the status says. This is the email agreeing with the
           document, which is the invariant that matters: a rep reads the PDF, and a customer has
           both in front of them.
           ⚠️ `free` is untouched — free is a real, deliberate zero. Only `value` is a claim about
           a number, and a claim of zero is not one. */
        if (_st === 'value' && _amt <= 0) return { status: 'pending', amount: 0 };
        return { status: _st, amount: _amt };
      }
    }
    /* No workstation_meta (an older draft, or one built outside the QW). The only
       signal left is the amount: absent means there is no fee line at all, a real
       number means a value was set. A zero falls back to 'pending', because FREE is
       only ever set through the QW, which writes the meta we just failed to find. */
    if (amountFallback == null) return { status: 'none', amount: 0 };
    var amt = parseFloat(amountFallback);
    if (isFinite(amt) && amt > 0) return { status: 'value', amount: amt };
    return { status: 'pending', amount: 0 };
  }

  /* ============================================
     Copy — the first-quote email
     ============================================ */

  /* The freight paragraph. Two fees, four states each, so the pairs that read badly
     when simply concatenated get a merged sentence and everything else falls back to
     one sentence per fee. */
  /* What the free-shipping promotion applies to, in the customer's words.

     ⛔ Counted from the items ACTUALLY NAMED in the sentence before it, not from the number
     of quotes or line items. Two quotes whose most expensive item is the same product name
     one item, so "these items" would contradict the line the reader just read (Ryan).

     Saunas get their own phrasing: a sauna quote's freight promotion covers the range, and
     "this item" reads oddly next to it. Requires EVERY named item to be a sauna — a mixed
     list is not "all saunas". */
  function _freeShipTarget(names) {
    var list = (names || []).filter(Boolean);
    if (list.length && list.every(function (n) { return /sauna/i.test(String(n)); })) return 'all saunas';
    return list.length > 1 ? 'these items' : 'this item';
  }

  /* "your proposal" for one, "either of the proposals" for two. Three or more cannot be
     "either", so it widens to "any of the proposals" — Ryan named the first two cases. */
  function _proposalPhrase(nQuotes) {
    if (nQuotes > 2) return 'any of the proposals';
    return nQuotes === 2 ? 'either of the proposals' : 'your proposal';
  }

  function _freightSentences(o) {
    var ship = o.ship, inst = o.inst;
    var names = o.names || [];
    var nQuotes = o.quoteCount || 1;
    var plural = nQuotes > 1;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — the DESTINATION, not "your location".
       See _shipDestination. `stateName` is still honoured so the exported helper does not
       break for a caller that has only a state; it produces "to Michigan", never the old
       "to your location in Michigan", which is the phrasing being retired. */
    var dest = o.destination || o.stateName || '';
    var where = dest ? ' to ' + dest : ' to your location';
    var theseItems = _freeShipTarget(names);
    // "The quote includes" / "The quotes include" — the subject changes with the
    // attachment count, so the verb has to as well.
    var subject = plural ? 'The quotes include' : 'The quote includes';
    var out = [];

    /* ⛔ A PENDING FREIGHT SAYS NOTHING AT ALL (Ryan 2026-09-13). It used to announce itself -
       "Freight is still being quoted to Renton, WA, so that cost is not on the proposal yet" -
       and the combined case announced both. Ryan's call is that the email simply does not raise
       it: a quote whose freight is not settled should read as a quote, not as an apology.
       `none` has always been silent, and now behaves identically.
       ⚠️ So there is no combined "both still being quoted" branch any more; a pending
       INSTALLATION still speaks for itself in its own block below. */
    if (ship.status === 'value' && inst.status === 'value') {
      out.push(subject + ' the cost of shipping' + where + ', along with installation.');
      return out;
    }

    if (ship.status === 'value') {
      out.push(subject + ' the cost of shipping' + where + '.');
    } else if (ship.status === 'free') {
      /* Ryan's wording, 2026-09-13. ⚠️ IT NAMES THE DESTINATION LIKE THE PAID LINE DOES, and it
         retires the old promotion sentence ("We are running a promotion right now for free
         shipping on these items…") along with that sentence's sauna special case - `theseItems`
         via `_freeShipTarget` said "all saunas" on an all-sauna quote. Both helpers are still
         exported and still used elsewhere; only this sentence stopped calling them. */
      out.push(subject + ' 100% free shipping' + where + '.');
    }
    /* ⛔ `pending` and `none` push nothing - see the note above. */

    if (inst.status === 'value') {
      out.push('Installation is included in the ' + (plural ? 'quotes' : 'quote') + ' as well.');
    } else if (inst.status === 'free') {
      out.push('Installation is included at no additional charge.');
    } else if (inst.status === 'pending') {
      out.push('Installation is still being priced, so that cost is not on the proposal yet.');
    }
    return out;
  }

  /* ============================================
     What the customer asked about (pre-quote)
     ============================================
     The first-quote email names ONE product per attached quote, because by then the quote
     itself is the document and the email is a cover note. The inquiry follow-up has no
     quote yet, so the products ARE the content: Ryan - "these emails will often be sent to
     someone who inquired about multiple things."

     SOURCE ORDER, most specific first:
       1. the deal's line items - whatever the rep put on the Workstation after reading the
          inquiry. This is the normal case and the only one that reflects a rep's judgement.
       2. the lead's sample selections - a sample request arrives with real handles on it
          (sampleItems, populated from the Shopify picker's line-item properties), so it can
          be linked even when nothing has been built yet.
     A lead's free-text form answers deliberately do NOT feed this. They carry no handle, so
     anything derived from them would be an unlinked guess at a product name, and the whole
     point of naming the products is that each one is a link the customer can click.

     No source at all is a legitimate outcome, not a failure: a general "what do you carry"
     inquiry has no products, and the copy drops to "the inquiry you sent over". */
  var _INQUIRY_ITEM_CAP = 12;

  function _inquiryProducts(details, index) {
    var out = [], seen = {};
    var idx = Array.isArray(index) ? index : [];
    function _push(title, handle, qty) {
      var shown = _displayName(title);
      if (!shown) return;
      var key = String(handle || shown).toLowerCase();
      var nameKey = 'name:' + shown.toLowerCase();
      if (seen[key] || seen[nameKey]) return;
      seen[key] = 1; seen[nameKey] = 1;
      var url = _linkForLine({ handle: handle, title: title }, idx);
      /* Vendor comes off the same index entry the link came from, so "is this ECORE?" is
         answered by the catalog rather than by the words on the quote line, which a rep is
         free to retype. No entry means no vendor, which the category test reads as "not
         all ECORE" - the safe direction. */
      var h = String(handle || '').toLowerCase();
      var entry = h ? idx.find(function (pr) { return pr && String(pr.h || '').toLowerCase() === h; }) : null;
      if (!entry) {
        var tl = String(title || '').toLowerCase().trim();
        entry = tl ? idx.find(function (pr) { return pr && String(pr.t || '').toLowerCase().trim() === tl; }) : null;
      }
      out.push({
        title: shown, url: url, vendor: (entry && entry.v) || '',
        /* ⛔ TRI-STATE, and the test is on the KEY not the value. true = the storefront
           says freight is quoted; false = it says shipping is included; UNDEFINED = this
           product is not in the index, or its entry predates the field, and we do not know.

           `entry ? !!entry.fq : undefined` was the bug: an entry with no `fq` key collapsed
           to false, so every product in a catalog cached before the field existed read as
           "ships free" and the freight question vanished from every email (Ryan caught it on
           a paver quote, 2026-08-19). Absence of a key is not a value. */
        freightQuoted: (entry && entry.fq !== undefined) ? !!entry.fq : undefined,
        /* Heaviest variant in pounds. Same key-presence tri-state as freightQuoted:
           0 means "Shopify holds no weight for this", undefined means "not in the index". */
        weightLb: (entry && entry.wt !== undefined) ? Number(entry.wt) || 0 : undefined,
        uom: (entry && entry.uom) || '',
        quantity: qty || 0,
      });
    }

    var lines = (details && details.quote && Array.isArray(details.quote.lineItems))
      ? details.quote.lineItems : [];
    lines.forEach(function (li) {
      if (!li || li.isSpacer) return;
      _push(li.title, li.handle, parseInt(li.quantity, 10) || 0);
    });

    if (!out.length) {
      var si = (details && details.leadContext && Array.isArray(details.leadContext.sampleItems))
        ? details.leadContext.sampleItems : [];
      si.forEach(function (it) { if (it) _push(it.name, it.handle); });
    }
    return out;
  }

  /* CUSTOM_INQUIRYSAMPLE_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — THE SAMPLES A LEAD PICKED,
     BY NAME. RW-1494-A was a sample request whose only record of what she picked was the deal row's
     `leadSamples` ("ECORE Performance UltraTile - 1" Rubber Tile — Grippin Gray - EL46"): no handle,
     and no quote lines yet, so `_inquiryProducts` found nothing, the opener said "your recent
     inquiry" and the subject said "equipment". Used only when `_inquiryProducts` found nothing.

     The sample name is the product, then " — ", then the colour. The product half is matched to
     the index by exact title, else the longest index title it starts with (10+ chars, so a short
     generic title never claims it); the colour is the customer's reading of it (`_floorColorLabel`,
     "Grippin Gray - EL46" -> "Grippin Gray"). Shown as "ECORE Performance UltraTile in Grippin Gray"
     - Ryan's own wording on RW-1494-A. ⚠️ No index entry = no link and no vendor; a name opening
     "ECORE" is still read as ECORE so the subject can say flooring. */
  function _sampleProducts(dq, index) {
    var titles = (dq && Array.isArray(dq.leadSamples)) ? dq.leadSamples : [];
    var idx = Array.isArray(index) ? index : [];
    var out = [], seen = {};
    titles.forEach(function (raw) {
      var name = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
      if (!name) return;
      var parts = name.split(/\s[—–]\s/);
      var product = parts[0].trim();
      var color = parts.length > 1 ? _floorColorLabel(parts.slice(1).join(' - ')) : '';
      var pl = product.toLowerCase();
      var entry = idx.find(function (p) { return p && String(p.t || '').toLowerCase().trim() === pl; }) || null;
      if (!entry) {
        var best = null;
        idx.forEach(function (p) {
          var t = String((p && p.t) || '').toLowerCase().trim();
          if (t.length < 10 || pl.indexOf(t) !== 0) return;
          if (!best || t.length > String(best.t || '').length) best = p;
        });
        entry = best;
      }
      var shown = _displayName(product) + (color ? ' in ' + color : '');
      var key = shown.toLowerCase();
      if (seen[key]) return;
      seen[key] = 1;
      out.push({
        title: shown, url: (entry && entry.u) || '',
        vendor: (entry && entry.v) || (/^ecore\b/i.test(product) ? 'ECORE' : ''),
        freightQuoted: (entry && entry.fq !== undefined) ? !!entry.fq : undefined,
        weightLb: (entry && entry.wt !== undefined) ? Number(entry.wt) || 0 : undefined,
        uom: (entry && entry.uom) || '',
        quantity: 0,
        sample: true,
      });
    });
    return out;
  }

  /* The 4+ lead-in. Ryan: "flooring products you found on our website" when they are ALL
     ECORE, "gym equipment you found on our website" for anything else. Deliberately an
     all-or-nothing test - a mixed basket of flooring and racks is not "flooring products",
     and calling it that would be the one sentence in the email that is factually wrong.
     More categories are coming; add them here, and keep 'gym equipment' as the floor so an
     unknown vendor can never produce a blank noun. */
  function _isEcore(vendor) { return /^ecore\b/i.test(String(vendor || '').trim()); }

  /* ⛔ WHAT THE freight-quoted TAG ACTUALLY MEANS, corrected 2026-08-19.
     Ryan: "the freight-quoted tag means it's definitely not parcel, but items without that
     tag are likely parcel but COULD be free depending ... non freight-quoted tagged items
     aren't necessarily free shipping, it's more that shipping isn't included at checkout
     then we need to calculate them later."

     So the tag carries ONE fact, not two:
       tagged   -> definitely NOT parcel. Shipping is not settled at checkout.
       untagged -> LIKELY parcel, and says NOTHING about whether shipping is free.

     ⛔ THE EARLIER `_allFreeShipping` READ THE ABSENCE OF THE TAG AS A FREE-SHIPPING
     PROMISE, AND IT IS GONE. That inference came from the product page's freight badge and
     it was wrong in both directions. Measured against the live catalog: all 20 saunas are
     UNTAGGED and carry no shipping tag of any kind, yet weigh 400 to 490 lb - so the old
     rule called a half-ton sauna "free shipping, and parcel", and would have suppressed the
     delivery paragraph on exactly the order that most needs a liftgate.

     ⚠️ THERE IS NO FREE-SHIPPING SIGNAL IN THE CATALOG AT ALL. Not a tag, not a metafield,
     nothing. Suppressing the paragraph for free-shipping items therefore cannot be built
     today - it needs a tag in Shopify first. When one exists, this is where it reads, and
     the only sentence that actually needs guarding is the COMMERCIAL variant's "this will
     affect the shipping rate on the quote"; the residential and unknown wordings ask about
     a loading dock and promise nothing about price, so they are safe either way. */
  /* ============================================
     PARCEL, FREIGHT, OR WE DO NOT KNOW
     ============================================
     Ryan's rule: "all of this should really only apply if any single item on the quote is
     over 110lbs. Or if a single item multiplied by its quantity is over 110 lbs. Anything
     under that is gray area but most likely to ship via parcel shipping."

     ⛔ THE TAG IS ABSOLUTE IN ONE DIRECTION ONLY. Ryan: "every item with Freight-Quoted tag
     on it will NEVER be parcel", and separately: "items without that tag are likely parcel
     but COULD be free depending." So a tagged item is settled; an untagged one is not, and
     weight has to finish the job.

     Neither signal is redundant, and each covers the other's blind spot:
       the TAG    settles the heavy things Shopify has no weight for. 26 of 35 freight-tagged
                  products - every ECORE rubber roll and turf tile - carry NO weight at all,
                  so weight alone would have promised FedEx for 2,000 sq ft of flooring.
       the WEIGHT settles everything the tag misses, and it misses a lot: all 20 saunas are
                  untagged and weigh 400 to 490 lb. It also adds the QUANTITY dimension the
                  tag cannot see - forty pounds ships parcel alone, freight when you order
                  five.

     THREE OUTCOMES, and the third is not a failure:
       'freight' - anything tagged, or any line over the threshold.
       'parcel'  - nothing tagged AND every line has a KNOWN weight under the threshold.
       'unknown' - an item we cannot weigh and cannot rule out.
     ⚠️ 'parcel' REQUIRES POSITIVE KNOWLEDGE OF EVERY LINE, because it is the only outcome
     that makes a promise ("should be able to ship via a parcel carrier like FedEx"). The
     freight wording only asks a question, so 'unknown' borrows it. */
  var _PARCEL_CEILING_LB = 110;

  function _shipMode(items) {
    var list = Array.isArray(items) ? items : [];
    if (!list.length) return 'unknown';
    var anyUnknown = false;
    for (var i = 0; i < list.length; i++) {
      var it = list[i] || {};
      if (it.freightQuoted === true) return 'freight';
      var w = it.weightLb;
      if (w === undefined || w === 0) { anyUnknown = true; continue; }
      var qty = it.quantity > 0 ? it.quantity : 1;
      if (w > _PARCEL_CEILING_LB || (w * qty) > _PARCEL_CEILING_LB) return 'freight';
    }
    return anyUnknown ? 'unknown' : 'parcel';
  }

  /* ⚠️ THE `unit:` TAG OUTRANKS THE TITLE. Merchandising sets unit:sqft / unit:tile /
     unit:paver deliberately, and it survives a product rename; a word in a title does not.
     The title regex stays as the fallback for anything not tagged.

     ⛔ THIS LIVES IN THE KERNEL, not in blocks.js where it started, because _categoryPhrase
     below needs it and the kernel loads first — blocks.js now delegates here. Two copies of
     "is this a paver" would be two answers to the noun in the same email's subject line and
     body. (Ryan 2026-08-19.) */
  var _PAVER_RX = /\b(paver|pavers)\b/i;
  function _isPaver(it) {
    if (it && it.uom) return it.uom === 'paver';
    return _PAVER_RX.test(String((it && it.title) || ''));
  }

  function _categoryPhrase(items) {
    var list = Array.isArray(items) ? items : [];
    if (!list.length) return 'gym equipment';
    /* ⛔ PAVERS ARE TESTED FIRST, and that ordering is the fix rather than an accident.
       Pavers carry the ECORE vendor, so an all-paver basket fell through to the flooring
       branch (or, when the vendor was blank, all the way to "gym equipment") — which is how
       the post-quote ladder's rung 3 came to ask a paver customer whether they were "still
       in the market for the gym equipment". (Ryan 2026-08-19.)

       Each branch stays ALL-OR-NOTHING, same as the flooring test always was: a mixed
       basket of pavers and racks is not "pavers", and calling it that would be the one
       sentence in the email that is factually wrong. */
    if (list.every(_isPaver)) return 'pavers';
    if (list.every(function (it) { return _isEcore(it.vendor); })) return 'flooring products';
    return 'gym equipment';
  }

  /* Where the customer wants it. workstation_meta.customer is what the rep actually typed
     on the Sold To / Ship To tabs, so it leads; the draft order's shipping address is the
     fallback for a deal built straight in Shopify. Nothing found means the freight question
     cannot be asked, and the template says so rather than mailing a blank. */
  /* ⛔ WHAT THE REP HAS ALREADY CONFIRMED ABOUT THE DESTINATION. The Workstation's freight
     panel carries three flags - loadingDock, forkLift, insideDelivery - and they are the
     AUTHORITATIVE answer to the question this email exists to ask. The supplier notification
     and the PO both print a liftgate line derived from them.

     ⚠️ ONLY A `true` MEANS ANYTHING. All three default to false on a fresh quote and nothing
     records whether a rep ever looked, so `false` reads as "not confirmed", never as "they
     have no dock". Ryan, on this exact template: "on these inquiry emails to someone I've
     never spoken to, it's VERY unlikely we would have checked one of those boxes for loading
     dock or forklift yet" - so this is a rare short-circuit, not the common path, and it must
     fail toward asking. */
  function _confirmedDelivery(details) {
    var dc = details && details.workstationMeta && details.workstationMeta.freight
      && details.workstationMeta.freight.deliveryConditions;
    if (!dc) return null;
    if (!dc.loadingDock && !dc.forkLift) return null;
    return {
      loadingDock: !!dc.loadingDock,
      forkLift: !!dc.forkLift,
      /* The words the supplier email uses for the same fact, so a rep who reads both never
         meets two vocabularies for one thing. */
      label: (dc.loadingDock && dc.forkLift) ? 'a loading dock and a forklift'
           : (dc.loadingDock ? 'a loading dock' : 'a forklift'),
    };
  }

  function _shipZip(details) {
    var wmz = details && details.workstationMeta && details.workstationMeta.customer
      && details.workstationMeta.customer.zip;
    if (wmz && String(wmz).trim()) return String(wmz).trim();
    var qz = details && details.quote && details.quote.shippingAddress && details.quote.shippingAddress.zip;
    return (qz && String(qz).trim()) ? String(qz).trim() : '';
  }

  /* ⛔ WHAT IS ACTUALLY AT THE DELIVERY ADDRESS. Not derivable here, and deliberately not
     read off the form: the lead form's residential/commercial choice is stored as
     customer.customerType, and Ryan watched it lie - "She filled out 'commercial', but when
     I Google the address I can clearly see it's a farm." The worker looks the address up
     with a web search and returns {kind, reason, ...}; this is only the fetch.

     Memoised per ref for the life of the page: the answer is a property of the address, and
     a template re-render must not spend a second search. ⚠️ A FAILED CALL IS NOT CACHED as
     "unknown" - it is dropped so the next open can try again, same rule as the send ledger. */
  var _addrCache = {};
  /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — `loose` is the address a LEAD carries on
     its dashboard row ({street, street2, city, state, zip, statedType}). A lead that has not been
     made into a deal has no workstation_meta, so the ref alone read "no address" and every lead
     got the "please confirm the shipping address" ending. The worker already takes loose fields
     beside a ref and prefers the deal's own address whenever one exists, so passing both is safe
     on a promoted deal too. */
  function _classifyAddress(ref, loose) {
    var key = String(ref || '').trim();
    if (!key) return Promise.resolve(null);
    if (_addrCache[key]) return _addrCache[key];
    var api = _api();
    /* Guarded on the METHOD, not just the object: an older cached custom-rep-portal.js
       without .post would throw here, and a template that throws leaves the rep with an
       error instead of a menu. No lookup is a degraded email; no menu is a broken one. */
    if (!api || typeof api.post !== 'function') return Promise.resolve(null);
    var body = { ref: key };
    if (loose) Object.keys(loose).forEach(function (k) { if (loose[k]) body[k] = String(loose[k]); });
    _addrCache[key] = api.post('/classify-delivery-address', body)
      .then(function (d) { return (d && d.success) ? d : null; })
      .catch(function () { _addrCache[key] = null; return null; });
    return _addrCache[key];
  }

  /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — THE CUSTOMER'S PHONE, AS WE HAVE IT.
     What the rep typed on the Workstation first, then the draft order, then the dashboard row
     (the only one a lead that was never made into a deal has). Read back to the customer by the
     wrong-number sentence, so it is the number exactly as it is on file — the point of that
     sentence is to show them what they typed. */
  function _customerPhone(details, dq) {
    var wm = details && details.workstationMeta && details.workstationMeta.customer;
    var q = details && details.quote && details.quote.customer;
    return String((wm && wm.phone) || (q && q.phone) || (dq && dq.customerPhone) || '').trim();
  }

  /* Could this be a real US / Canadian number? NANP: ten digits (a leading 1 is the country
     code), and neither the area code nor the exchange may start with 0 or 1. Ryan's RW-1444-A
     left "(166) 133-3077", which fails both. ⚠️ A WARNING TO THE REP, NEVER COPY: an overseas
     number fails this too, so nothing may tell a customer their number is wrong on this test. */
  function _phoneLooksReal(phone) {
    var d = String(phone || '').replace(/\D/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    return d.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(d);
  }

  /* The rep's own phone, for "call, or text: ". Read from /rep-settings, which resolves
     it exactly the way the signature does, so the number in the body and the number under
     it can never disagree. Memoised for the life of the page: it is a property of the rep,
     not of the deal, so template-menu invalidation deliberately does not clear it. */
  var _repSettings = null;
  function _loadRepSettings() {
    if (_repSettings) return _repSettings;
    var api = _api();
    if (!api) return Promise.resolve({});
    _repSettings = api.get('/rep-settings')
      .then(function (d) { return d || {}; })
      .catch(function () { _repSettings = null; return {}; });
    return _repSettings;
  }

  /* ============================================
     GATING (Ryan 2026-08-19)
     ============================================
     Two of these templates are once-per-customer, and the picker greys them out rather
     than hiding them, so a rep can see WHY a row is closed instead of wondering where it
     went. A gate returns null when the row is live, or the sentence shown under it.

     THE TWO FACTS A GATE RUNS ON

     1. HAS A QUOTE BEEN PRODUCED? Ryan's rule, verbatim: "when the status is yet to change
        to QUOTED ... we know there has not yet been a quote." That is exactly `chainQuoted`
        on the dashboard row - PDF downloaded, or status quote_sent, or the pill flipped by
        hand - so this reuses the same predicate dashboard-main.js already runs for the
        cadence ladder, rather than inventing a second definition of "quoted" free to drift
        from the first. sold/lost count too: you do not reach either without quoting.
        Chain-wide, because a quote on -A is a quote to this customer.

     2. HAS IT ALREADY GONE OUT? From the send ledger the worker writes at send time
        (`/email-templates-sent`), keyed by chain base. It is NOT read off the sent email:
        the noteEntry for an outbound message is written minutes later by the Outlook sync
        cron, and a Graph message carries no memory of which template produced it.

     ⛔ AND THE LEDGER READ RACES THE REP. A KV write is eventually consistent; a rep who
     sends and immediately reopens the composer can beat it. `_markSentLocal` records the
     send in this page as well, and the gate reads the union, so the row is dead the instant
     Send succeeds no matter what KV thinks yet.

     ⛔ "Fully deleted the template and wrote their own email" is NOT a send of the template.
     The modal only stamps templateId when what it inserted is still recognisably there -
     see _templateSurvived in dashboard-email-modal.js. Ryan: "if a template is added then
     changed/added to then that's fine ... added then fully deleted then we can assume the
     template wasn't sent." */
  function _chainQuoted(ref) {
    var dq = _dealByRef(ref);
    var deals = _chainDeals(ref).concat(dq ? [dq] : []);
    return deals.some(function (q) {
      if (!q) return false;
      return q.lifecycleStatus === 'quoted' || q.lifecycleStatus === 'sold' || q.lifecycleStatus === 'lost'
        || !!q.chainQuoted || !!q.pdfDownloaded || !!q.chainPdfDownloaded;
    });
  }

  /* ============================================
     CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — A QUOTE THAT COULD BE PRODUCED, AS
     OPPOSED TO ONE THAT ALREADY HAS BEEN
     ============================================
     Ryan on RW-1333-A: *"I am yet to send them a quote... Now, I'm in the DD I click the email
     button and I open the email composer, and the 'First quote to a customer' template is grayed
     out. Any idea why?"*

     ⛔ `_chainQuoted` ABOVE MEANS "THE CUSTOMER HAS BEEN QUOTED", AND ON THIS BOARD THAT IS
     LITERALLY `pdfDownloaded && !(sold||lost)`. RW-1333-A had `lifecycleStatus: null` and
     `pdfDownloaded: false` — a real, saved, priced quote (`total: 3056.90`, `lastSavedAt` 8/28)
     that had simply never been rendered to a document. Both once-only checks were clean: the
     `email-templates-sent:RW-1333` ledger key did not exist and no note entry carried a PDF. It
     was the FIRST check that shut the row.

     ⛔ AND ON THE DEAL DASHBOARD THAT WAS UNESCAPABLE, BECAUSE THE GATE ASKED FOR THE THING THE
     GATE PREVENTED. `first-quote` carries `needsQuotePdf: true`, so picking it makes the composer
     fetch `/pdf` and attach the quote — but `_ensureQuotePdfAttached` runs on PICK, and the row
     renders with a real `disabled` attribute. The only two things that opened check 1 were the
     deal already being quoted, or a quote already on the email; on a never-rendered deal neither
     could become true. The 2026-08-25 `quoteAttached` escape hatch only reaches the Quote
     Workstation's Send-Quote screen, which pre-attaches before the picker opens.

     📌 SO THE FIX IS A THIRD, SEPARATE FACT — NOT A WIDER `quoted`. ⛔ Widening `_chainQuoted`
     would have been the tempting one-word change and it would have LIED to two other gates:
     `email-tpl-inquiry.js` refuses the inquiry follow-up with *"The quote has already gone out,
     so this one is no longer relevant"*, which would then fire on a deal whose quote has never
     left the building. Same case, different branch. So `quoted` keeps its meaning exactly and
     this answers the different question the first check was always really asking.

     ⚠️ THE SIGNAL IS THE BOARD'S OWN, NOT A NEW INVENTION. `chainLastSavedAt` is precisely what
     `quote-dashboard.js` and `dashboard-main.js` use to decide a row has stopped "awaiting a
     quote" — dashboard-main.js:9647 says in as many words that the signal is `chainLastSavedAt`
     and NOT `chainHasItems`. Items are accepted too because a chain rollup can carry one without
     the other, and either alone means there is something to render.

     ⚠️ A FALSE OPEN COSTS A WARNING, NOT A BAD EMAIL. If this opens the row on a deal `/pdf`
     cannot render, `_ensureQuotePdfAttached` already fails non-fatally: the rep gets *"the quote
     PDF could not be attached automatically"* and the manual attach menu, which is exactly what
     the Workstation's own Send Quote does. That asymmetry is why erring open is right here.

     ⛔ IT CANNOT CAUSE A DOUBLE SEND. Check 1 is an "is there anything to send" guard; the
     once-only guards are checks 2 and 3 (`wasSent`, `quotePdfAlreadyEmailed`), which are separate
     and still run untouched. */
  function _chainQuotable(ref) {
    return _dealSet(ref).some(function (q) {
      return !!(q && (q.chainLastSavedAt || q.lastSavedAt || q.chainHasItems || q.hasItems));
    });
  }

  function _baseRef(ref) { return String(ref || '').trim().replace(/-[A-Z]$/i, ''); }

  /* base ref -> { templateId: ISO }, this page only. ⚠️ A TIMESTAMP, not a flag: the
     follow-up ladder asks "has anything come back since follow-up 2 went out?", which is a
     date question, and the local record has to answer it in the same shape the ledger does
     or the union would silently lose the date on a just-sent email. */
  var _sentLocal = {};
  function _markSentLocal(ref, id) {
    var base = _baseRef(ref);
    if (!base || !id) return;
    if (!_sentLocal[base]) _sentLocal[base] = {};
    _sentLocal[base][id] = new Date().toISOString();
  }

  var _sentCache = {};                       // base ref -> Promise<{ templateId: iso }>
  function _loadTemplatesSent(ref) {
    var base = _baseRef(ref);
    if (!base) return Promise.resolve({});
    if (_sentCache[base]) return _sentCache[base];
    var api = _api();
    if (!api) return Promise.resolve({});
    _sentCache[base] = api.get('/email-templates-sent?ref=' + encodeURIComponent(base))
      .then(function (d) { return (d && d.sent && typeof d.sent === 'object') ? d.sent : {}; })
      .catch(function () {
        /* ⛔ A failed read must not cache as "nothing has been sent" - that is the answer
           that OPENS a row which should be closed. Drop the memo and let the next open
           try again; one extra KV read is cheaper than a duplicate email. */
        _sentCache[base] = null;
        return {};
      });
    return _sentCache[base];
  }

  /* ⛔ THE LEDGER ONLY KNOWS ABOUT SENDS THAT HAPPENED AFTER IT EXISTED.
     Every deal already in flight today has quotes that went out with no templateId on
     them, and reading the ledger alone would offer "First quote to a customer" on a job
     the customer was quoted three weeks ago. So the gate also asks the deal: has an
     outbound email carrying a PDF for this chain already left the building?

     Read off the note log the Outlook sync writes, matched on the attachment FILENAME the
     same way the composer's attach menu identifies a quote - every path that produces one
     names it from the /pdf Content-Disposition, which leads with the ref.

     Deliberately ONE-WAY: it can only ever close a row, never open one. And deliberately
     scoped to first-quote, because "a quote PDF has gone out" is precisely what that
     template claims and says nothing about any other template. */
  function _quotePdfAlreadyEmailed(ref) {
    var base = _baseRef(ref);
    if (!base) return false;
    var rx = new RegExp(base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(-[A-Z])?\\b', 'i');
    var dq = _dealByRef(ref);
    var deals = _chainDeals(ref).concat(dq ? [dq] : []);
    return deals.some(function (q) {
      var entries = (q && Array.isArray(q.noteEntries)) ? q.noteEntries : [];
      return entries.some(function (n) {
        var em = n && n.emailMeta;
        if (!em || em.direction !== 'outbound') return false;
        var names = Array.isArray(em.attachmentNames) ? em.attachmentNames : [];
        return names.some(function (nm) { return /\.pdf$/i.test(String(nm || '')) && rx.test(String(nm || '')); });
      });
    });
  }

  /* ============================================
     THE FACTS EVERY GATE RUNS ON
     ============================================
     Built once per picker open and handed to every gate, so ten templates cost one chain
     load and one ledger read rather than ten of each. A gate is a pure function of this
     object; it must not fetch. */
  /* ⛔ EVERY READER OF THE NOTE LOG MUST GO THROUGH HERE. `_chainDeals(ref)` already
     contains the composer's own deal (it filters the loaded deals by base ref), so the
     `_chainDeals(ref).concat(dq)` shape that reads fine for "the most recent call" silently
     DOUBLES anything counted: a rep who left two voicemails got told they had left a few.
     Deduped on reference, with an identity fallback for a deal that somehow has none. */
  function _dealSet(ref) {
    var dq = _dealByRef(ref);
    var all = _chainDeals(ref).concat(dq ? [dq] : []);
    var seen = [], out = [];
    all.forEach(function (q) {
      if (!q) return;
      var key = q.reference || q;
      if (seen.indexOf(key) !== -1) return;
      seen.push(key);
      out.push(q);
    });
    return out;
  }

  function _gateState(ref, sent, ctx) {
    var base = _baseRef(ref);
    var local = _sentLocal[base] || {};
    var deals = _dealSet(ref);
    var atts = (ctx && Array.isArray(ctx.attachments)) ? ctx.attachments : [];

    function sentAt(id) {
      /* The local record wins when both exist: it is this page, and it cannot be behind. */
      var v = local[id] || (sent && sent[id]) || '';
      return v ? String(v) : '';
    }

    return {
      ref: ref,
      base: base,
      deals: deals,
      quoted: _chainQuoted(ref),
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — is there a quote here that COULD be
         produced? Distinct from `quoted`, which asks whether one already has been. See the long
         note on `_chainQuotable` for why widening `quoted` instead would have broken the inquiry
         follow-up's refusal wording on a deal whose quote never went out. */
      quotable: _chainQuotable(ref),
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — is a quote for this chain ON THIS EMAIL?

         ⛔ `quoted` ASKS A QUESTION THAT ONLY BECOMES TRUE AFTER THE SEND IT GATES. It reads the
         deal's status, and on the Quote Workstation's Send-Quote flow the status flips to QUOTED
         as a RESULT of the email being composed — so on the one screen where "First quote to a
         customer" is exactly the right template, it was guaranteed unavailable. Ryan hit it on
         RW-1342-A: a deal created that afternoon, never sent, no status tag, with the freshly
         rendered PDF already sitting in the composer. Every template greyed except the one that
         REQUIRES an unquoted deal (inquiry follow-up), which reads as "gated behind the inquiry
         email" and is not.

         Matched on the attachment FILENAME, not on the X-BSF header stamp, deliberately: the
         question here is "does this email carry a quote for this chain", which a filename answers
         on its own. The stamp can be absent for reasons that have nothing to do with it (a
         re-attach replayed out of Outlook), and a gate that needed it would fail closed again.

         ⚠️ It can only ever OPEN first-quote, never close anything: the once-only rules
         (`wasSent`, `quotePdfAlreadyEmailed`) are separate checks and still run. And it cannot
         open the row on a deal with no quote, because there would be no quote PDF to attach. */
      quoteAttached: _attachedQuoteRefs(atts, ref).length > 0,
      wasSent: function (id) { return !!sentAt(id); },
      sentAt: sentAt,
      quoteEmail: _quoteEmailSend(ref, deals),
      lastInboundAfter: function (isoOrMs) { return _lastInboundAfter(deals, isoOrMs); },
      contactSince: function (isoOrMs) { return _contactSince(deals, isoOrMs); },
      lastOutboundEmail: function () { return _lastOutboundEmail(deals); },
      lastOutboundAfter: function (isoOrMs) { return _lastOutboundAfter(deals, isoOrMs); },
    };
  }

  /* ============================================
     Reading the email thread off the note log
     ============================================
     Outbound and inbound messages both land on `noteEntries` as `{type:'email', emailMeta}`,
     written by the Outlook sync cron. ⚠️ THAT CRON RUNS EVERY 5 MINUTES, so anything here
     is a picture of the thread as of a few minutes ago. Every consumer of these helpers is
     asking a question measured in days, so that is fine - but nothing here may ever be used
     to decide something about the last five minutes. The send LEDGER exists precisely
     because that question needed a different answer. */
  function _msOf(v) {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    var t = Date.parse(v);
    return isNaN(t) ? 0 : t;
  }

  function _emailEntries(deals) {
    var out = [];
    (deals || []).forEach(function (q) {
      var entries = (q && Array.isArray(q.noteEntries)) ? q.noteEntries : [];
      entries.forEach(function (n) {
        if (!n || !n.ts || !n.emailMeta) return;
        out.push(n);
      });
    });
    out.sort(function (a, b) { return _msOf(a.ts) - _msOf(b.ts); });
    return out;
  }

  /* THE quote send: the most recent outbound email that carried a PDF for this chain. Most
     recent rather than first, because if a revised quote went out later that is the quote
     the customer is sitting on, and "the quote I sent you" has to mean that one.
     Identified by the attachment FILENAME, the same way the attach menu identifies a quote:
     every path that produces one names it from the /pdf Content-Disposition, which leads
     with the ref. So this is right no matter which door the file came through. */
  function _quoteEmailSend(ref, deals) {
    var base = _baseRef(ref);
    if (!base) return null;
    var rx = new RegExp(base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(-[A-Z])?\\b', 'i');
    var best = null;
    _emailEntries(deals).forEach(function (n) {
      var em = n.emailMeta;
      if (!em || em.direction !== 'outbound') return;
      var names = Array.isArray(em.attachmentNames) ? em.attachmentNames : [];
      var pdfs = names.filter(function (nm) { return /\.pdf$/i.test(String(nm || '')) && rx.test(String(nm || '')); });
      if (!pdfs.length) return;
      if (!best || _msOf(n.ts) > _msOf(best.ts)) {
        best = { ts: n.ts, messageId: em.messageId || '', ref: n.originRef || ref, quoteFiles: pdfs, subject: em.subject || '' };
      }
    });
    return best;
  }

  function _lastOutboundEmail(deals) {
    var best = null;
    _emailEntries(deals).forEach(function (n) {
      if (n.emailMeta.direction !== 'outbound') return;
      if (!best || _msOf(n.ts) > _msOf(best.ts)) best = n;
    });
    return best;
  }

  /* ⛔ ANY inbound reply closes the ladder, not just one from the To line. Ryan framed the
     trigger as "the person who was in the TO field of the quote never responded", and a
     stricter read would be to match the address. But a reply from a CC'd colleague means
     the thread is ALIVE, and mailing a "did you get this?" into a live thread is the one
     outcome the ladder exists to avoid. Failing toward silence is the safe direction. */
  function _lastInboundAfter(deals, since) {
    var t0 = _msOf(since);
    var best = null;
    _emailEntries(deals).forEach(function (n) {
      if (n.emailMeta.direction !== 'inbound') return;
      if (_msOf(n.ts) <= t0) return;
      if (!best || _msOf(n.ts) > _msOf(best.ts)) best = n;
    });
    return best;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — the mirror of _lastInboundAfter: did WE
     write back after a given moment? Together they answer "is the ball in our court", which is
     what decides the first-quote email's opening pleasantry.

     ⚠️ The email being composed right now is NOT in here, and must not be — it is the reply,
     and it is not a note entry until it sends. So "unreplied" means unreplied as of the moment
     the rep started typing, which is exactly the question the opener is asking. */
  function _lastOutboundAfter(deals, since) {
    var t0 = _msOf(since);
    var best = null;
    _emailEntries(deals).forEach(function (n) {
      if (n.emailMeta.direction !== 'outbound') return;
      if (_msOf(n.ts) <= t0) return;
      if (!best || _msOf(n.ts) > _msOf(best.ts)) best = n;
    });
    return best;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — the same two questions, asked only of mail the
     CUSTOMER was actually on.

     ⛔ "AN EMAIL ON THE DEAL" IS NOT "AN EMAIL WITH THE CUSTOMER", and on a live deal the two
     diverge by hours. RW-1296-A: the buyer wrote at 13:22 and got no answer; the rep emailed ECORE
     for freight at 14:53. `_lastOutboundAfter` sees an outbound 91 minutes after the inbound and
     reports the customer was answered — so the first-quote email would drop its acknowledgement on
     the strength of a message the customer never saw. The inbound side is worse: the newest inbound
     on that deal is the VENDOR's 16:01 reply, so an unfiltered read would thank the buyer for an
     email ECORE sent us.

     ⚠️ DELIBERATELY NEW FUNCTIONS RATHER THAN A FILTER BOLTED ONTO THE OLD ONES. `_lastInboundAfter`
     is participant-blind ON PURPOSE — the follow-up ladder uses it, and its rule is that ANY reply
     closes the ladder, including one from a CC'd colleague, because mailing "did you get this?"
     into a live thread is the outcome it exists to prevent. Narrowing it there would be a
     regression dressed as a fix. Two questions, two functions.

     `addrs` is a lowercase Set. An empty one answers null rather than everything: a deal with no
     recorded address cannot prove any email was the customer's, and inventing an acknowledgement
     is worse than omitting one. */
  function _isCustomerEmailEntry(n, addrs) {
    var em = n && n.emailMeta;
    if (!em || !addrs || !addrs.size) return false;
    var parties = [em.from].concat(
      Array.isArray(em.to) ? em.to : [],
      Array.isArray(em.cc) ? em.cc : []
    );
    for (var i = 0; i < parties.length; i++) {
      var a = String(parties[i] == null ? '' : parties[i]).trim().toLowerCase();
      if (a && addrs.has(a)) return true;
    }
    return false;
  }
  function _lastCustomerEmailAfter(deals, since, addrs, direction) {
    var t0 = _msOf(since);
    var best = null;
    _emailEntries(deals).forEach(function (n) {
      if (n.emailMeta.direction !== direction) return;
      if (_msOf(n.ts) <= t0) return;
      if (!_isCustomerEmailEntry(n, addrs)) return;
      if (!best || _msOf(n.ts) > _msOf(best.ts)) best = n;
    });
    return best;
  }
  function _lastCustomerInboundAfter(deals, since, addrs) {
    return _lastCustomerEmailAfter(deals, since, addrs, 'inbound');
  }
  function _lastCustomerOutboundAfter(deals, since, addrs) {
    return _lastCustomerEmailAfter(deals, since, addrs, 'outbound');
  }

  /* Every address that IS this customer, lowercased. `RepPortal.Contacts.contactEmails` is the
     multi-contact definition (primary + every secondary), and it is the theme's byte-for-byte twin
     of the worker's lib/contacts.js — so a buyer who looped in a colleague, or who writes from a
     second address the rep recorded, is still recognised. Falls back to splitting the comma-joined
     mirror by hand on a page that never booted the portal shell. */
  function _customerAddrs(quoteCustomer, dq, toEmails) {
    var out = new Set();
    var push = function (v) {
      String(v == null ? '' : v).split(/[,;\s]+/).forEach(function (a) {
        var k = String(a).trim().toLowerCase();
        if (k && k.indexOf('@') > 0) out.add(k);
      });
    };
    try {
      var C = window.RepPortal && window.RepPortal.Contacts;
      if (C && typeof C.contactEmails === 'function' && quoteCustomer) {
        C.contactEmails(quoteCustomer).forEach(push);
      } else if (quoteCustomer) { push(quoteCustomer.email); }
    } catch (_) { if (quoteCustomer) push(quoteCustomer.email); }
    if (dq) push(dq.customerEmail);
    /* ⚠️ The composer's To line counts too. On the Workstation the synthesised feed carries the
       chain's `customerEmail`, but a rep who corrected the address in the To box before writing is
       telling us who this email is for. */
    (Array.isArray(toEmails) ? toEmails : []).forEach(push);
    return out;
  }

  /* Everything we have tried since a moment: the calls, whether a voicemail was left, the
     texts, and how many emails went out. This is what lets a follow-up say "I called and
     left you a voicemail last week" instead of pretending the phone does not exist.
     `last` is the most recent ATTEMPT (call or text) - the one an email should reference. */
  function _contactSince(deals, since) {
    var t0 = _msOf(since);
    var out = { vms: [], noVms: [], connected: [], texts: [], emailsOut: 0, last: null };
    (deals || []).forEach(function (q) {
      var entries = (q && Array.isArray(q.noteEntries)) ? q.noteEntries : [];
      entries.forEach(function (n) {
        if (!n || !n.ts || _msOf(n.ts) <= t0) return;
        var types = Array.isArray(n.types) && n.types.length ? n.types : (n.type ? [n.type] : []);
        if (n.emailMeta && n.emailMeta.direction === 'outbound') { out.emailsOut++; return; }
        if (n.emailMeta) return;                       // inbound email is not an attempt
        if (types.indexOf('call') !== -1) {
          if (n.callOutcome === 'no-answer') (n.callVm === 'left' ? out.vms : out.noVms).push(n);
          else out.connected.push(n);
          if (!out.last || _msOf(n.ts) > _msOf(out.last.ts)) out.last = n;
        } else if (types.indexOf('text') !== -1) {
          out.texts.push(n);
          if (!out.last || _msOf(n.ts) > _msOf(out.last.ts)) out.last = n;
        }
      });
    });
    return out;
  }

  /* ============================================
     THE REGISTRY
     ============================================
     Templates register themselves; this file knows none of them by name. Order in the
     picker is REGISTRATION order, which is the order the sections load the files in, so
     "which email comes first in the pecking order" is answered by the <script> order in
     rep-quote-dashboard.liquid rather than by an array someone has to remember to edit. */
  var TEMPLATES = [];

  function register(def) {
    if (!def || !def.id || typeof def.build !== 'function') {
      console.warn('[email-tpl] ignored a template with no id or no build()', def);
      return;
    }
    if (TEMPLATES.some(function (t) { return t.id === def.id; })) {
      /* A duplicate id would make the ledger ambiguous - two templates sharing one
         "already sent" fact. Loud, because it can only be a copy-paste. */
      console.error('[email-tpl] duplicate template id: ' + def.id);
      return;
    }
    TEMPLATES.push(def);
  }

  /* ⛔ The per-ref memos must not outlive the rep's intent. They exist so one build (and
     the re-renders an attachment change triggers) does not re-hit Shopify - not so that a
     quote edited between two template inserts keeps answering with its old freight.
     Opening the template menu is a fresh intent, so the modal calls this first.
     ⚠️ _repSettings is deliberately NOT cleared: it is a property of the rep, not the deal. */
  function _invalidate() { _detailsCache = {}; _sentCache = {}; }

  /* ============================================
     THE SNIPPET REGISTRY (Ryan 2026-08-19)
     ============================================
     Ryan: *"I want to add some email snippets - like 1-2 sentences that can be inserted
     wherever in an email. Sort of like how HubSpot has these."*

     ⛔ A SNIPPET IS NOT A SMALL TEMPLATE, and the difference is the whole design:

       template  →  OWNS the body. Replaces it. Once per email. Greyed when unavailable,
                    because "you cannot send this yet" is the answer the rep wanted.
       snippet   →  lands AT THE CARET, mid-sentence, as many times as the rep likes,
                    and knows nothing about what surrounds it.

     So there is no gate here, no send ledger, and no once-only rule: those all exist to
     answer "has this EMAIL gone out", and a snippet is not an email. What a snippet does
     share with a template is the output shape - runs, never HTML - because the reason for
     that is unchanged: a sentence can carry a product hyperlink, and the link has to ride
     as an inline format on those characters.

     A snippet declares EITHER `text` (the common case: a fixed sentence, with {{tokens}})
     or `build(ctx)` (when it has to read the deal, count something, or link). Registration
     order is picker order, exactly as for templates.

     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — a `build` snippet may ALSO declare
     `options(ctx)`, which makes it a two-step snippet: the modal opens a searchable picker and
     hands the chosen row back as `ctx.option` on the same build. See `RQDSnippets.hasOptions`.
     ⚠️ `options` without `build` is not a snippet — the guard below still demands one of the two,
     because a picker whose choice nothing renders is a menu that does nothing. */
  var SNIPPETS = [];

  function registerSnippet(def) {
    if (!def || !def.id || (!def.text && typeof def.build !== 'function')) {
      console.warn('[email-tpl] ignored a snippet with no id and no text/build()', def);
      return;
    }
    if (SNIPPETS.some(function (s) { return s.id === def.id; })) {
      console.error('[email-tpl] duplicate snippet id: ' + def.id);
      return;
    }
    SNIPPETS.push(def);
  }

  /* ── the token set ────────────────────────────────────────────────────────
     Deliberately SHORT. Every token is a thing a rep would otherwise retype on every
     email, and every one has a fallback that still reads as English, because the
     alternative - a snippet that renders "Hi ," - is worse than a slightly generic
     sentence. A token that had to fall back also raises a warning, so the rep is told
     the sentence went out generic rather than discovering it in the sent folder.

     ⚠️ `product` is the only token that can carry a LINK, which is why tokens resolve to
     RUNS and not to strings. */
  /* Bare noun phrases, because they are dropped into "No ___ on this deal, so that part reads
     generically." A leading "the" here produces "No the customer's first name on this deal". */
  var TOKEN_HELP = {
    first_name: 'customer first name',
    full_name: 'customer name',
    rep_name: 'name for you',
    rep_phone: 'phone number for you',
    quote_ref: 'quote reference',
    product: 'priced product',
  };

  function _tokenValues(ctx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    return Promise.all([_loadDetails(ref), _loadRepSettings(), _loadMentionIndex(), _ensureChain(ref)])
      .then(function (r) {
        var details = r[0] || null, settings = r[1] || {}, index = r[2] || [];
        var dq = _dealByRef(ref);
        var quote = (details && details.quote) || null;
        var full = String((quote && quote.customer && quote.customer.name) || (dq && dq.customer) || '').trim();
        if (full.indexOf('@') !== -1) full = '';       // an email address is not a name
        var first = _firstName(full);
        if (!first) {
          /* Same last resort the first-quote template uses: a To address often carries the
             name when the draft order does not. */
          var to = (Array.isArray(ctx && ctx.toEmails) ? ctx.toEmails : [])[0] || '';
          first = _firstName(String(to).split('@')[0].replace(/[._-]+/g, ' '));
        }
        var top = _mostExpensiveLine((quote && quote.lineItems) || []);
        var prodName = top ? _displayName(top.title) : '';
        var prodUrl = top ? _linkForLine(top, index) : '';

        return {
          first_name: { runs: first ? [{ t: first }] : [{ t: 'there' }], missing: !first },
          full_name: { runs: full ? [{ t: full }] : [{ t: 'there' }], missing: !full },
          rep_name: { runs: [{ t: settings.repName || '' }], missing: !settings.repName },
          rep_phone: { runs: [{ t: settings.repPhone || '' }], missing: !settings.repPhone },
          quote_ref: { runs: [{ t: ref }], missing: !ref },
          /* ⚠️ The fallback carries NO article. A snippet writes "the {{product}}" or "your
             {{product}}" itself, and a fallback of "the equipment" turns the first into
             "the the equipment". Articles belong to the sentence, not to the token. */
          product: {
            runs: prodName ? [{ t: prodName, link: prodUrl || undefined }] : [{ t: 'equipment' }],
            missing: !prodName,
          },
        };
      });
  }

  /* "Hi {{first_name}}, ..." → runs, with the link on {{product}} preserved. Splitting on
     the token pattern rather than string-replacing is what makes that possible: a replace
     would flatten the product into plain text and lose the href. */
  var _TOKEN_RX = /\{\{\s*([a-z_]+)\s*\}\}/g;

  function _renderTokens(text, values) {
    var runs = [], warnings = [], last = 0, m;
    _TOKEN_RX.lastIndex = 0;
    while ((m = _TOKEN_RX.exec(String(text || ''))) !== null) {
      if (m.index > last) runs.push({ t: String(text).slice(last, m.index) });
      var v = values[m[1]];
      if (!v) {
        /* An unknown token is a typo in a snippet definition, not a data problem. It goes
           through VERBATIM so the rep sees "{{frist_name}}" in the body and deletes it,
           rather than silently mailing a gap where a name should have been. */
        runs.push({ t: m[0] });
        warnings.push('"' + m[0] + '" is not a token this snippet system knows, so it went in as written.');
      } else {
        v.runs.forEach(function (r) { if (r.t) runs.push(r); });
        if (v.missing) warnings.push('No ' + (TOKEN_HELP[m[1]] || m[1]) + ' on this deal, so that part reads generically.');
      }
      last = m.index + m[0].length;
    }
    if (last < String(text || '').length) runs.push({ t: String(text).slice(last) });
    return { runs: runs.filter(function (r) { return r.t; }), warnings: warnings };
  }

  /* ── what an ATTACHED quote PDF says about itself ─────────────────────────
     The modal stamps every quote PDF it fetches with the page the ORDERING INSTRUCTIONS
     section landed on and the card link that PDF actually printed, read off the /pdf
     response headers (see _quoteMetaFromHeaders in dashboard-email-modal.js). This is the
     kernel's read of that, in attachment order, so a snippet can describe the documents
     riding on THIS email rather than the deal in the abstract.

     ⛔ Deduped by ref, because attaching the same revision twice is a rep mistake the
     customer must not read about ("page 4 of the $12,000 proposal, and page 4 of the
     $12,000 proposal"). */
  function _attachedQuoteMeta(attachments) {
    var out = [], seen = {};
    (Array.isArray(attachments) ? attachments : []).forEach(function (a) {
      var m = a && a.quoteMeta;
      if (!m || !m.ref || !m.orderingPage) return;
      var k = String(m.ref).toUpperCase();
      if (seen[k]) return;
      seen[k] = 1;
      out.push(m);
    });
    return out;
  }

  /* ============================================
     THE ARRANGEMENT — what a row is CALLED, what ORDER it sits in, whether it is OFFERED
     CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS suggestion 88a57d3920c4, Ryan 2026-09-03)
     ============================================
     The read-only catalog tab shipped saying what came next: *"Renaming and reordering are next,
     then editing the wording."* This is that half. The wording is still code.

     ⛔ THE ID NEVER MOVES. Only the label does. `first-quote` is what the send ledger writes,
     what the once-only gate asks about, and what the follow-up sequence names its rungs with — a
     rename that rewrote the id would silently re-open a template a customer had already been
     sent, because "has this gone out" would be asked of a name nothing was ever logged under.

     ⛔ AND IT IS APPLIED HERE, IN THE REGISTRY, NOT ON THE ADMIN PAGE. A rename that only moved
     the label on the Abby Emails tab would be a lie: the rep opening the ⚡ menu on the Deal
     Dashboard would still read the old name. Every surface that loads this file gets the same
     arrangement, because every surface reads it through these projections.

     ⚠️ THE FETCH IS ASYNC AND `list` IS A SYNC GETTER, so the two answer differently for the
     handful of milliseconds before `/composer-catalog` lands. That is deliberate and it is why
     `list` and `listFor` split the way they do, below. */
  var _META = null;         // the arrangement, or null while it is still unknown
  var _metaP = null;        // the memoised fetch

  function _copy(o) {
    var c = {}, k;
    for (k in o) if (Object.prototype.hasOwnProperty.call(o, k)) c[k] = o[k];
    return c;
  }

  /* ⛔ REPLACE, DO NOT APPEND. An admin who edits a snippet and refreshes the meta would
     otherwise get a second copy of it — `registerSnippet` would refuse the duplicate id, log an
     error nobody is watching, and leave the OLD wording in the picker behind a green "Saved". */
  function _syncCustomSnippets(list) {
    for (var i = SNIPPETS.length - 1; i >= 0; i--) {
      if (SNIPPETS[i] && SNIPPETS[i].custom) SNIPPETS.splice(i, 1);
    }
    (list || []).forEach(function (c) {
      if (!c || !c.id || !c.text) return;
      /* ⚠️ `text`, never `build`. A rep-authored row is a fixed sentence with {{tokens}} — the
         shape the snippet contract already calls the preferred one. Nothing arriving from KV can
         become code that runs. */
      registerSnippet({
        id: c.id, icon: c.icon || '', label: c.label || c.id,
        keywords: c.keywords || '', text: c.text, custom: true,
      });
    });
  }

  function _setMeta(m) {
    _META = m ? {
      labels: m.labels || {},
      hidden: m.hidden || {},
      order: {
        template: (m.order && m.order.template) || [],
        snippet: (m.order && m.order.snippet) || [],
      },
      custom: Array.isArray(m.custom) ? m.custom : [],
      /* CUSTOM_HUMANCOPYEDIT_BY_CLAUDE — edited sentences, keyed by phrase id. Rides in the
         same record as the arrangement because it is the same screen and the same admin; it is
         written by its own route so a rename staged beside it cannot clobber it. */
      phrases: (m.phrases && typeof m.phrases === 'object') ? m.phrases : {},
      updatedAt: m.updatedAt || null,
      updatedBy: m.updatedBy || null,
      canEdit: !!m.canEdit,
    } : null;
    _syncCustomSnippets(_META ? _META.custom : []);
    return _META;
  }

  function _loadMeta() {
    if (_metaP) return _metaP;
    var api = _api();
    /* ⚠️ NOT MEMOISED WHEN THERE IS NO API YET. The shared portal script is deferred and on some
       pages parses AFTER this one, so the first caller can genuinely arrive before RepPortal
       exists. Caching "unknown" here would pin the page to the code defaults for its whole life. */
    if (!api) return Promise.resolve(_META);
    _metaP = api.get('/composer-catalog')
      .then(function (d) { return (d && d.success) ? _setMeta(d) : _META; })
      .catch(function () {
        /* ⛔ A FAILED READ MUST NOT CACHE AS "there are no overrides". That reads as a clean
           answer and is not one — the menus would silently show the built-in names, and the
           first symptom would be a rep insisting they had renamed something. Clearing the memo
           means the next open of the picker tries again. */
        _metaP = null;
        return _META;
      });
    return _metaP;
  }

  /**
   * Apply the arrangement to a set of registry rows.
   *
   * @param rows       projected rows, each carrying `id` and `label`
   * @param kind       'template' | 'snippet' — order is kept per kind
   * @param keepHidden true for the LOOKUP projections, false for the PICKER ones. See below.
   *
   * ⛔ AN ID MISSING FROM `order` KEEPS ITS REGISTRATION POSITION AND SORTS LAST. A template
   * added in code next month appears at the end of the menu: visible, not silently first, and
   * never silently gone. The index tiebreak is explicit rather than leaning on Array#sort being
   * stable — it is on every engine that matters, and writing it down costs one comparison.
   */
  function _arrange(rows, kind, keepHidden) {
    if (!_META) return rows;
    var m = _META, out = [];
    rows.forEach(function (r) {
      var hid = !!m.hidden[r.id];
      if (hid && !keepHidden) return;
      var lbl = m.labels[r.id];
      if (!hid && !lbl) { out.push(r); return; }
      var c = _copy(r);
      if (lbl) c.label = lbl;
      if (hid) c.hidden = true;
      out.push(c);
    });

    var seq = (m.order && m.order[kind]) || [];
    if (!seq.length) return out;
    var pos = {};
    seq.forEach(function (id, i) { pos[id] = i; });
    return out.map(function (r, i) { return { r: r, i: i }; })
      .sort(function (a, b) {
        var pa = pos[a.r.id], pb = pos[b.r.id];
        if (pa === undefined && pb === undefined) return a.i - b.i;
        if (pa === undefined) return 1;
        if (pb === undefined) return -1;
        return pa - pb;
      })
      .map(function (x) { return x.r; });
  }

  /* Every projection below is a `_arrange(..., keepHidden)` call and the flag is the whole
     distinction between them:

       PICKER projections (`listFor`, `search`) DROP hidden rows. "Do not offer this" is the
       thing an admin asked for, and these are the surfaces where offering happens.

       LOOKUP projections (`list`, `catalog`) KEEP them, flagged `hidden: true`. `list` is how
       the modal answers "does THIS id need the quote PDF?" and how the sequence card finds the
       label for a rung it already holds by id — a lookup that could not find a hidden row would
       fall back to printing the raw id on screen. `catalog` keeps them because un-hiding one is
       the admin page's whole job.

     ⚠️ WHICH MEANS `list` CAN RENDER A HIDDEN ROW if a caller treats it as a menu. The one place
     that does is the modal's fallback when `listFor` rejects, and it filters on `hidden` there. */

  /* ============================================
     THE SENTENCES — what a template SAYS, separated from what it DECIDES
     CUSTOM_HUMANCOPYEDIT_BY_CLAUDE (AIOPS suggestion 88a57d3920c4 follow-on, Ryan 2026-09-03)
     ============================================
     The card asked for editable wording and the read-only tab said why it could not have it yet:
     all thirteen of these BUILD their words from the deal, so there was no stored sentence
     anywhere to put in a text box. This is the answer to that, and the shape of the answer is
     the whole point.

     ⛔ THE EDITABLE UNIT IS A WHOLE SENTENCE, NEVER A FRAGMENT. These builders concatenate
     prose around values — `' regarding the ' + category + ' you found on our website:'` — and
     exposing those pieces would be worse than exposing nothing. That trailing colon is there
     because BULLETS FOLLOW IT; the leading space belongs to the word before. Somebody tidying a
     fragment would break a layout they could not see from the box they were typing in. So a
     phrase is declared as one sentence with its values as {TOKENS}, and the fragments stop
     existing.

     ⛔ AND THE BRANCH STAYS IN CODE. "Did they leave a voicemail", "are there four products or
     one", "has this deal got a ship-to" — those are decisions about a deal, not wording, and
     they keep living in the builder. What the builder gets to do is CHOOSE a sentence; what it
     no longer does is spell one.

     ⚠️ WHICH MEANS VARIANTS MUST BE EDITED TOGETHER, and the naming is what makes that
     automatic. A dot in the key groups siblings: `arrived.dated` and `arrived.undated` are one
     group, and the editor renders a group as a set. The inquiry template's own comment already
     warned about this in prose — *"a wording change has to land on all three or the email
     quietly changes voice with the size of the quote"* — and nothing enforced it. Now the
     screen does.

     ⛔ SINGLE BRACES, `{LIKE_THIS}` — NOT the `{{lower}}` of the snippet vocabulary. Two
     reasons, and neither is taste. The snippet form is a SHIPPED rep-facing contract with six
     fixed names, while these are a per-sentence vocabulary the code declares; and a doubled
     brace cannot be written in `sections/rep-abby.liquid`, which has to display these, because a
     .liquid file is parsed as Liquid before it is a script. `abby-tokens.js` already made the
     same call for the same first reason.

     ⛔ AN OVERRIDE THAT LOSES A TOKEN FALLS BACK TO THE BUILT-IN SENTENCE, VISIBLY. Every token
     here is a value the code computed from the deal, so dropping one does not shorten the
     sentence — it silently stops saying the thing the sentence existed to say. The editor
     refuses that save; this is the second layer, and it warns the rep looking at the draft
     rather than quietly serving them the old words. Where dropping a value is a legitimate
     wording choice, the sibling variant already exists for it. */
  var _PHRASE_RX = /\{([A-Z][A-Z0-9_]*)\}/g;
  var PHRASES = [];
  /* TOKEN -> what its value actually is, contributed by whichever file declares it first.
     Shown beside the editor's box; see the note on definePhrases for why one of them matters
     more than the rest. */
  var _TOKEN_NOTES = {};

  /* ⛔ WARNINGS COLLECT HERE AND ARE DRAINED BY `build`, rather than being threaded back through
     every builder. Each of these builders already owns a `warnings` array it pushes into by
     hand, and a phrase failure is not the builder's business — it is the composer's. Reset at
     the top of a build and drained at the bottom, so nothing can leak into the next one. */
  var _phraseWarnings = [];
  function _phraseWarn(msg) {
    if (_phraseWarnings.indexOf(msg) === -1) _phraseWarnings.push(msg);
  }
  function _drainPhraseWarnings() {
    var out = _phraseWarnings;
    _phraseWarnings = [];
    return out;
  }

  /** Render one sentence to RUNS. A token's value may be a string or a runs array — the second
   *  is what lets a product name inside a sentence carry its hyperlink. */
  function _phraseRuns(text, values, id) {
    var s = String(text == null ? '' : text), runs = [], last = 0, m;
    _PHRASE_RX.lastIndex = 0;
    while ((m = _PHRASE_RX.exec(s)) !== null) {
      if (m.index > last) runs.push({ t: s.slice(last, m.index) });
      var v = values ? values[m[1]] : undefined;
      if (v === undefined || v === null) {
        /* Verbatim, with a warning — the same call the snippet renderer makes, for the same
           reason: a visible "{WHEN}" in the draft is deleted before it is sent, and a silent
           gap where a date should have been is not. */
        runs.push({ t: m[0] });
        _phraseWarn('"' + m[0] + '" is not a value this sentence is given, so it went into the '
          + 'draft as written (' + id + ').');
      } else if (Array.isArray(v)) {
        v.forEach(function (r) { if (r && r.t) runs.push(r); });
      } else {
        var t = String(v);
        if (t) runs.push({ t: t });      // an empty value contributes nothing, by design
      }
      last = m.index + m[0].length;
    }
    if (last < s.length) runs.push({ t: s.slice(last) });
    return runs.filter(function (r) { return r.t; });
  }

  function _renderPhrase(id, def, tokens, values) {
    var stored = (_META && _META.phrases) ? _META.phrases[id] : null;
    if (stored && stored !== def) {
      /* ⛔ MEASURED AGAINST THE SENTENCE'S OWN DECLARED TOKENS, not against whatever the caller
         happened to pass. A builder may hand the same values object to two variants — the
         "just now" rendering of a call reference has no {WHEN} in it — and checking the caller's
         keys would report that variant as having lost a token it never had. The question is
         "does the edit still say everything the built-in sentence said", and only the default
         can answer it. */
      var lost = (tokens || []).filter(function (k) {
        return String(stored).indexOf('{' + k + '}') === -1;
      });
      if (lost.length) {
        _phraseWarn('Your saved wording leaves out {' + lost.join('}, {') + '}, so this draft is '
          + 'the built-in sentence instead (' + id + ').');
      } else {
        return _phraseRuns(stored, values, id);
      }
    }
    return _phraseRuns(def, values, id);
  }

  /**
   * Declare a family file's copy, and get back one render function per sentence.
   *
   *   var COPY = T.definePhrases('follow-up-2', {
   *     'reviewed': "Hopefully you've had a chance to review the {QUOTE_WORD} I sent over.",
   *   });
   *   paras.push(COPY.reviewed({ QUOTE_WORD: r.words.quote }));
   *
   * ⛔ DECLARED AT MODULE LEVEL, NOT INLINE AT THE CALL SITE. The editor has to be able to list
   * every sentence WITHOUT building all thirteen emails against a real deal first, and a phrase
   * that only registers when its branch happens to run is a phrase the admin page cannot show.
   * The side effect is the better half of the trade: a family file now opens with its whole
   * email readable in one block, and the builder below it is pure assembly.
   *
   * @param owner  the template id, or 'blocks' for copy every email shares
   * @param map    key -> default sentence. A dot in the key groups variants.
   * @param help   optional TOKEN -> what it is. Worth writing for any token whose value is not
   *               self-evident from its name.
   *
   * ⚠️ AND ESPECIALLY FOR A TOKEN THAT CARRIES ITS OWN LEADING SPACE. `{WHEN}` is " yesterday"
   * or the empty string, so the sentence is written `...voicemail{WHEN}.` with no space in front
   * of it. That looks like a typo, and an admin who "fixes" it gets a double space on every
   * dated send and a space before the full stop on every undated one. The note is what stops
   * that; the live preview beside the box is what shows it.
   */
  function definePhrases(owner, map, help) {
    var api = {};
    if (help) {
      Object.keys(help).forEach(function (k) {
        if (!_TOKEN_NOTES[k]) _TOKEN_NOTES[k] = String(help[k]);
      });
    }
    Object.keys(map || {}).forEach(function (key) {
      var text = String(map[key]);
      var id = owner + '.' + key;
      if (PHRASES.some(function (p) { return p.id === id; })) {
        /* A duplicate would make one stored override govern two sentences. Loud, because it can
           only be a copy-paste. */
        console.error('[email-tpl] duplicate phrase id: ' + id);
        return;
      }
      var tokens = [], m;
      _PHRASE_RX.lastIndex = 0;
      while ((m = _PHRASE_RX.exec(text)) !== null) if (tokens.indexOf(m[1]) === -1) tokens.push(m[1]);
      var dot = key.lastIndexOf('.');
      PHRASES.push({
        id: id, owner: owner, key: key, text: text, tokens: tokens,
        group: dot === -1 ? id : owner + '.' + key.slice(0, dot),
      });
      api[key] = function (values) { return _renderPhrase(id, text, tokens, values); };
    });
    return api;
  }

  /** Every declared sentence, for the admin page. Grouped by `group`; `source` says whether the
   *  words on screen are the built-in ones or an edit. */
  function _phraseCatalog() {
    return PHRASES.map(function (p) {
      var stored = (_META && _META.phrases) ? _META.phrases[p.id] : null;
      return {
        id: p.id, owner: p.owner, key: p.key, group: p.group,
        text: (stored != null ? stored : p.text),
        defaultText: p.text,
        tokens: p.tokens.map(function (t) {
          return { name: t, note: _TOKEN_NOTES[t] || '' };
        }),
        source: (stored != null && stored !== p.text) ? 'stored' : 'code',
      };
    });
  }

  /* Everything a template file is allowed to reach for. Named WITHOUT the underscore
     prefix: inside this file a leading _ means "module-private", and anything published
     here is by definition not. */
  window.RQDTpl = {
    register: register,
    registerSnippet: registerSnippet,
    renderTokens: _renderTokens, tokenValues: _tokenValues, tokenHelp: TOKEN_HELP,
    attachedQuoteMeta: _attachedQuoteMeta,

    // data
    api: _api, loadDetails: _loadDetails, loadMentionIndex: _loadMentionIndex,
    loadRepSettings: _loadRepSettings, ensureChain: _ensureChain,
    dealByRef: _dealByRef, chainDeals: _chainDeals, dealSet: _dealSet, baseRef: _baseRef,

    // people + places
    firstName: _firstName, properName: _properName, stateName: _stateName, shipZip: _shipZip,
    shipDestination: _shipDestination,
    confirmedDelivery: _confirmedDelivery,
    customerPhone: _customerPhone, phoneLooksReal: _phoneLooksReal,

    // time
    whenPhrase: _whenPhrase, callWhenPhrase: _callWhenPhrase, ordinal: _ordinal,
    /* ⛔ THE CLOCK EVERY TEMPLATE READS, AND IT IS NOT ALWAYS `Date.now()` (Ryan 2026-08-20).
       Ryan: *"if the 'send within business hours' box is checked, which in a given circumstance
       is going to result in the email going out tomorrow instead, does the tool know to change
       stuff like 'i left a voicemail today' to 'i left a voicemail yesterday'?"*

       It did not. A held email is composed at 9pm and delivered at 6am, and every phrase in it
       ("just now", "earlier today", "a little bit ago") was resolved against the moment the rep
       typed rather than the moment the customer reads. So the copy arrived describing yesterday
       in today's words.

       ⛔ THE FIX IS TO WRITE THE EMAIL FOR WHEN IT WILL ARRIVE, not to patch the words afterwards.
       Re-dating a frozen string at send time is impossible anyway — the copy lives client-side,
       and Quill strips any marker span we could have wrapped a phrase in (PM³³). Moving the
       CLOCK instead costs one function and makes the preview honest too: the rep sees
       "yesterday" on screen before they send it.

       ⚠️ Set by RQDTemplates.build from `ctx.nowMs` and cleared straight after, so nothing can
       leak a stale clock into the next build. */
    now: function () { return _nowOverride || Date.now(); },

    // the note log
    lastSpokenCall: _lastSpokenCall, lastNoAnswerCall: _lastNoAnswerCall,

    // products
    displayName: _displayName, linkForLine: _linkForLine, mostExpensiveLine: _mostExpensiveLine,
    floorColorLabel: _floorColorLabel,
    inquiryProducts: _inquiryProducts, sampleProducts: _sampleProducts, categoryPhrase: _categoryPhrase, isEcore: _isEcore,
    lineUom: _lineUom,   // CUSTOM_ROLLSENTENCE — a line's unit: tag, for the layout email's roll sentence
    isPaver: _isPaver,
    shipMode: _shipMode, parcelCeilingLb: _PARCEL_CEILING_LB,
    classifyAddress: _classifyAddress,

    /* the ECORE resources page — the live document index behind the install-instructions snippet.
       ⚠️ `ecoreDocs()` is the SYNC peek and never fetches, which is what lets a snippet's
       `available()` consult it; `loadEcoreDocs()` is the async read. */
    loadEcoreDocs: _loadEcoreDocs, refreshEcoreDocs: _refreshEcoreDocs, ecoreDocs: _ecoreDocsNow,
    ecoreManualUrl: _ecoreManualUrl, ecoreManualName: _ecoreManualName,
    ecoreBrandedName: _ecoreBrandedName, ecoreCoversList: _ecoreCoversList,
    ecoreManualsForQuote: _ecoreManualsForQuote, ecoreSquash: _ecoreSq,
    ecoreDocsUrl: _ECORE_DOCS_URL,

    // quotes + money
    attachedQuoteRefs: _attachedQuoteRefs, liveFees: _liveFees, feeState: _feeState,
    freightSentences: _freightSentences, freeShipTarget: _freeShipTarget,
    proposalPhrase: _proposalPhrase,

    // sentence plumbing
    joinRuns: _joinRuns,

    // the email thread, off the note log
    quoteEmailSend: _quoteEmailSend, lastOutboundEmail: _lastOutboundEmail,
    lastInboundAfter: _lastInboundAfter, lastOutboundAfter: _lastOutboundAfter,
    lastCustomerInboundAfter: _lastCustomerInboundAfter,
    lastCustomerOutboundAfter: _lastCustomerOutboundAfter,
    customerAddrs: _customerAddrs,
    contactSince: _contactSince,

    // gate facts
    chainQuoted: _chainQuoted, quotePdfAlreadyEmailed: _quotePdfAlreadyEmailed,
    /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — the send ledger itself, for a BUILD that
       needs more than "was this id sent": the 2nd+ quote email reads `_layout` off it (the last
       floor plan that went out — see recordQuoteLayoutSent in the worker). Memoised per chain
       and dropped on every menu open, same as the gate's read. */
    loadTemplatesSent: _loadTemplatesSent,

    /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — the arrangement, for the admin page that edits it.
       `loadMeta()` resolves it; `meta()` is the loaded value or null; `setMeta(record)` installs
       what a save just returned so the tab re-renders from the same object the picker will use,
       rather than from a hopeful local copy of what was posted. */
    loadMeta: _loadMeta,
    meta: function () { return _META; },
    setMeta: _setMeta,

    /* CUSTOM_HUMANCOPYEDIT_BY_CLAUDE — the sentences. `definePhrases` is what a family file
       calls at module level; `phrases()` is what the admin page lists. */
    definePhrases: definePhrases,
    phrases: _phraseCatalog,
  };

  /* The modal-facing contract. Unchanged in shape from the single-file era on purpose:
     splitting the implementation must not make the caller learn anything new. */
/* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE (AIOPS suggestion 88a57d3920c4, Ryan 2026-09-03)
   ────────────────────────────────────────────────────────────────────────
   `catalog` — the projection the Abby Emails page's "Templates & snippets" tab reads.

   ⛔ A SECOND PROJECTION, NOT A WIDER `list`. `list` is the composer picker's contract and its
   own note says a field is invisible to the modal until it is named there — so widening it to
   carry catalog fields would put things on the picker's rows that the picker must never branch
   on, and the next person to add a modal behaviour would find them sitting there looking usable.
   Different consumer, different question, different projection.

   ⛔ AND IT IS A READ OF THE LIVE REGISTRY, WHICH IS THE WHOLE POINT. The card that asked for
   the catalog was explicit: *"One source of truth. Do not clone them into a second store that can
   drift."* These templates and snippets ARE this code; a list of them typed out anywhere else
   would be a description that goes stale the first time somebody edits a sentence here. So the
   page loads these same files and asks the registry what it is holding.

   ⚠️ `lastEdited` IS DELIBERATELY ABSENT, and the card asked for it. There is no such fact: a
   registration is source code, and its last edit is a git commit, not a field. Emitting a
   plausible date would be worse than emitting none — see `source: 'code'`, which is the honest
   answer to the same question.

   ⚠️ NEITHER IS A SUBJECT LINE. A template owns the BODY; the composer owns the subject, and no
   registration here declares one. The tab says so rather than rendering an empty column. */

  window.RQDTemplates = {
    invalidate: _invalidate,

    /* Every template as the CATALOG needs it. See the note above for why this is separate from
       `list`, and for what is deliberately not in it.
       CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — now ARRANGED (renamed, reordered) and KEEPING hidden
       rows, flagged. Un-hiding one is the admin page's whole job, so a projection that dropped
       them would make hiding a one-way door. `defaultLabel` rides along because "what was this
       called before I renamed it" is the second question anybody renaming something asks, and
       because it is what a Reset button puts back. */
    get catalog() {
      return _arrange(TEMPLATES.map(function (t) {
        return {
          kind: 'template',
          id: t.id,
          icon: t.icon || '',
          label: t.label || t.id,
          defaultLabel: t.label || t.id,
          hint: t.hint || '',
          /* ⛔ EVERY ONE OF THESE SENDS AS THE HUMAN AT THE COMPOSER, never as Abby, and saying so
             on every row is the card's third bullet. The distinction is not cosmetic: these live
             in the same module as 22 emails that DO send as Abby Rivers, and a reader who mixes
             the two up will reword a customer-facing automation thinking it is a rep's snippet. */
          sendsAs: 'the person composing',
          /* A template always builds — `register` refuses one without a build() — so there is no
             fixed text to show. `hint` is the nearest thing to a description. */
          bodyKind: 'built',
          text: '',
          /* Whether it can be greyed out on a given deal. A reader deciding whether wording is
             safe to change wants to know the row carries logic as well as sentences. */
          conditional: typeof t.gate === 'function',
          needsQuotePdf: !!t.needsQuotePdf,
          /* ⚠️ `source` ANSWERS "WHERE DO THESE WORDS COME FROM", AND IT IS STILL `code` EVEN ON A
             RENAMED ROW. Renaming moves a menu label; it does not touch a syllable of the email.
             `labelSource` below is the separate, smaller question. */
          source: 'code',
        };
      }), 'template', true).map(function (r) {
        r.labelSource = (r.label === r.defaultLabel) ? 'code' : 'stored';
        r.hidden = !!r.hidden;
        r.custom = false;                 // a template can never be rep-authored — see the store
        return r;
      });
    },

    get list() {
      /* ⚠️ THIS IS A PROJECTION, so a field a template declares is INVISIBLE to the modal until
         it is named here. `needsQuotePdf` (2026-08-29) was added to the first-quote template and
         read by the composer's _ensureQuotePdfAttached, and it arrived undefined until this line
         carried it — the flag was set, the lookup found the row, and the row simply did not have
         it. Anything the modal must branch on belongs in this map.

         CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — arranged, and KEEPING hidden rows with `hidden: true`
         on them. ⛔ This is a LOOKUP projection: the modal asks it "does this id need the quote
         PDF?" and the sequence card asks it "what is rung `follow-up-2` called?" — both by an id
         they already hold. A `list` that dropped a hidden row would answer the second with
         nothing, and the raw id would go on screen. The one caller that uses it as a MENU (the
         fallback when `listFor` rejects) filters on `hidden` itself.

         ⚠️ AND IT IS SYNC, so before /composer-catalog lands it answers with the built-in names.
         That is the safe direction: a real label, briefly not the renamed one. `listFor` awaits
         the arrangement and is what every picker actually calls. */
      return _arrange(TEMPLATES.map(function (t) {
        return { id: t.id, icon: t.icon, label: t.label, hint: t.hint, needsQuotePdf: !!t.needsQuotePdf };
      }), 'template', true);
    },

    /* Every template with its availability ON THIS DEAL resolved. Rows come back in
       registry order and always ALL of them: a template a rep cannot use today is still
       something they should be able to see, greyed, with the reason under it. */
    listFor: function (ctx) {
      var ref = String((ctx && ctx.ref) || '').trim();
      /* ⛔ ensureChain FIRST. _chainQuoted reads the loaded deals synchronously, and on the
         Quote Workstation those are not there until the chain resolves - so without this the
         picker asks "has this been quoted?" of an empty list, gets no, and greys out First
         Quote on the very page a rep just built the quote on. Same trap that dropped the
         thank-you line from the Workstation before _dealByRef went through the union. */
      /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — `_loadMeta` joins the same Promise.all rather than
         being awaited after it. It is memoised and almost always already resolved, and putting it
         here means the picker never paints a row under its old name while the arrangement is in
         flight. ⚠️ It never rejects — a failed read resolves to whatever is known — so it cannot
         send this whole call down the modal's ungated fallback. */
      return Promise.all([_ensureChain(ref), _loadTemplatesSent(ref), _loadMeta()]).then(function (r) {
        var st = _gateState(ref, r[1] || {}, ctx);
        return _arrange(TEMPLATES.map(function (t) {
          var reason = null;
          try { reason = t.gate ? t.gate(st) : null; } catch (e) {
            /* A throwing gate must not blank the menu. Fail OPEN on the row but say so:
               a rep who can see the template is better off than a rep with an empty picker,
               and the console line is how we find out. */
            console.error('[email-tpl] gate threw for ' + t.id, e);
            reason = null;
          }
          return {
            id: t.id, icon: t.icon, label: t.label, hint: t.hint,
            disabled: !!reason, disabledReason: reason || '',
          };
        /* ⛔ HIDDEN ROWS ARE DROPPED HERE, and that is a different decision from the greying
           above. Greyed means "not on THIS deal, and here is why" — the answer a rep opened the
           menu for. Hidden means an admin took the row out of the menu, so there is no reason to
           show and nothing about this deal would bring it back. */
        }), 'template', false);
      });
    },

    /* Called by the modal the moment a send carrying a template succeeds, so the row is
       dead in this page before the worker's KV write has finished propagating. */
    markSent: _markSentLocal,

    build: function (id, ctx) {
      var t = TEMPLATES.find(function (x) { return x.id === id; });
      if (!t) return Promise.reject(new Error('Unknown template: ' + id));
      var c = ctx || {};
      /* ⛔ THE CLOCK IS SET AROUND THE BUILD AND CLEARED IN A `finally`. `ctx.nowMs` is how a
         caller says "write this email for a moment that is not now" — the business-hours hold
         passes the scheduled send time so "earlier today" comes out as "yesterday". A build that
         throws must still restore the clock, or every template rendered afterwards in this page
         would quietly date itself to tomorrow morning. */
      _nowOverride = Number(c.nowMs) || 0;
      /* CUSTOM_HUMANCOPYEDIT_BY_CLAUDE — ⛔ THE SENTENCE WARNINGS ARE DRAINED HERE, and this is
         the only place they can be. A phrase that fell back to its built-in wording has to reach
         the rep looking at the draft, and no builder knows it happened: `_renderPhrase` is three
         layers down inside a block. Cleared BEFORE the build so nothing from the last one leaks
         in, and appended to whatever the builder collected itself.
         ⚠️ The drain runs on the resolved value, not in the `finally`, because `finally` cannot
         change what comes out. The clock still resets either way. */
      _drainPhraseWarnings();
      try {
        return Promise.resolve(t.build(c))
          .then(function (out) {
            var extra = _drainPhraseWarnings();
            if (extra.length) {
              out = out || {};
              out.warnings = (out.warnings || []).concat(extra);
            }
            return out;
          })
          .finally(function () { _nowOverride = 0; });
      } catch (e) {
        _nowOverride = 0;
        _drainPhraseWarnings();
        return Promise.reject(e);
      }
    },

    /* For the harness, and for anything that wants to reason about the set. */
    _internals: window.RQDTpl,
  };

  /* ============================================
     THE SNIPPET CONTRACT THE MODAL SEES (Ryan 2026-08-19)
     ============================================
     window.RQDSnippets.list            → [{id, icon, label}] in registration order (no hint)
     window.RQDSnippets.search(q)       → the same rows, filtered + ranked for the "#" picker
     window.RQDSnippets.build(id, ctx)  → Promise<{paragraphs, warnings}>
       ctx = { ref, attachments, toEmails }   ← identical to the template ctx on purpose

     `paragraphs` is the template shape, and a one-sentence snippet is simply one paragraph.
     The modal inserts it AT THE CARET instead of replacing the body; nothing here knows or
     cares which. Same runs, same "no HTML crosses this boundary" rule. */
  /* ⛔ NO `hint`. Ryan 2026-08-19: *"the snippets don't need sub-text... just the main text
     header is fine."* A template row carries one because it writes a whole email and can be
     greyed out with a reason; a snippet is one sentence and its label is the description.
     The field is gone rather than merely unrendered, so nobody writes one that never shows.
     ⚠️ `keywords` is NOT the same thing - it is invisible and feeds the "#" search. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ A SNIPPET THAT WOULD REFUSE IS GREYED
     BEFORE IT IS CLICKED. Ryan, on the two weight-design version snippets: *"should be grayed out,
     if we didn't actually make them a new version."* A snippet refuses by THROWING, which the
     picker turns into a status line — correct, and it arrives after the click, so the row reads as
     available right up until it is not.

     ⚠️ THIS IS NOT THE `hint` FIELD, which is gone on purpose (see the note above) and stays gone:
     the reason rides as a `title` tooltip, so a greyed row is still ONE LINE. Ryan asked for no
     sub-text on snippets and a disabled state does not earn an exception.

     ⚠️ `available()` MUST NOT THROW and must be cheap — it runs for every row on every open of the
     picker. It reads what is already loaded; anything that needs a fetch belongs in `build`, which
     is allowed to be slow and allowed to refuse. */
  function _snippetRow(s, ctx) {
    var row = { id: s.id, icon: s.icon || '', label: s.label };
    if (typeof s.available === 'function') {
      var why = '';
      try { why = s.available(ctx || {}) || ''; } catch (e) { why = ''; }
      if (why) { row.disabled = true; row.disabledReason = String(why); }
    }
    return row;
  }

  /* The haystack fields `search` hangs on a row while it ranks. Stripped on the way out so the
     picker's row shape stays exactly what it has always been — a field the modal can see is a
     field somebody will eventually branch on. */
  function _stripSearchFields(r) {
    delete r._keys; delete r._body; delete r._was;
    return r;
  }

  /* CUSTOM_SHIPMENTSPEC_BY_CLAUDE (LOGAN 5ea246b1adfb, Ryan 2026-10-31) - ⛔ A SNIPPET MAY READ BEFORE THE MENU PAINTS.
     `available()` must stay sync and cheap, so a snippet whose grey-out needs a fetch (the freight details: "is there a
     rated shipment on this deal?") declares `prepare(ctx)`, and the menu waits for it - BOUNDED at 1.5 s, so a slow read
     paints the row available (fail OPEN, build is the authority) instead of holding the menu. Ryan asked for the grey on
     the first open, not the second. A rejection is swallowed: it is a warm-up, never a verdict. */
  var PREPARE_WAIT_MS = 1500;
  function _prepareSnippets(ctx) {
    var jobs = SNIPPETS.filter(function (s) { return typeof s.prepare === 'function'; }).map(function (s) {
      var p;
      try { p = Promise.resolve(s.prepare(ctx || {})); } catch (e) { p = Promise.resolve(); }
      return Promise.race([p.catch(function () {}), new Promise(function (r) { setTimeout(r, PREPARE_WAIT_MS); })]);
    });
    return Promise.all(jobs);
  }

  window.RQDSnippets = {
    invalidate: _invalidate,

    /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — see the note above RQDTemplates.catalog.
       ⚠️ THE SNIPPET HALF IS THE USEFUL HALF, because a `text` snippet carries its actual
       sentence and can be READ on the page. A `build` one cannot: its words are assembled from
       the deal at insert time, so the honest thing to show is that it is computed, not a
       best-effort rendering of it against no deal. */
    get catalog() {
      return _arrange(SNIPPETS.map(function (s) {
        var isText = typeof s.text === 'string' && s.text.length > 0;
        return {
          kind: 'snippet',
          id: s.id,
          icon: s.icon || '',
          label: s.label || s.id,
          defaultLabel: s.label || s.id,
          hint: s.keywords || '',
          sendsAs: 'the person composing',
          bodyKind: isText ? 'text' : 'built',
          text: isText ? s.text : '',
          conditional: typeof s.available === 'function',
          needsQuotePdf: false,
          /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — `stored` means the snippet EXISTS only as data:
             somebody wrote it on the admin page and it has no source file. That is the one row
             kind that can be edited word for word and deleted outright, which is why the
             distinction is on the row rather than inferred from the id prefix at the render. */
          source: s.custom ? 'stored' : 'code',
        };
      }), 'snippet', true).map(function (r) {
        r.labelSource = (r.label === r.defaultLabel) ? 'code' : 'stored';
        r.hidden = !!r.hidden;
        r.custom = (r.source === 'stored');
        return r;
      });
    },
    /* ⚠️ `list` cannot pass a ctx — it is a getter with nowhere to take one. `listFor(ctx)` is
       what the picker uses so `available()` can see the deal; `list` stays for anything that only
       wants the names. CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — like the template `list`, it is the
       LOOKUP projection and keeps hidden rows flagged. */
    get list() { return _arrange(SNIPPETS.map(function (s) { return _snippetRow(s); }), 'snippet', true); },

    /* ⛔ NOW ASYNC, AND SO IS `search`. CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — both were sync getters
       over an array that only code could change; a rep-authored snippet arrives over the wire, so
       a menu painted before /composer-catalog lands would simply be missing it. A snippet an
       admin wrote and cannot find is the exact failure this feature would be blamed for.
       ⚠️ THE "#" PICKER ALREADY AWAITED THIS — `fetchRows` wrapped it in `Promise.resolve` for a
       future rep-authored set, in so many words. The ⚡ menu did not, and now paints "Loading
       snippets…" first, the way the template menu beside it always has. */
    listFor: function (ctx) {
      return Promise.all([_loadMeta(), _prepareSnippets(ctx)]).then(function () {
        return _arrange(SNIPPETS.map(function (s) { return _snippetRow(s, ctx); }), 'snippet', false);
      });
    },

    /* Ranked, not merely filtered: with the query in the LABEL a snippet is what the rep is
       reaching for, and with it only in the keywords or the body text it is a maybe. Typing
       "#" with nothing after it lists everything in registration order, which is how a rep
       who does not yet know the names browses the set. */
    search: function (q, ctx) {
      /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — async now, and it ranks the ARRANGED rows. See the
         note on `listFor`. */
      return Promise.all([_loadMeta(), _prepareSnippets(ctx)]).then(function () {
        var query = String(q || '').trim().toLowerCase();
        /* Arranged before anything is scored, so an empty "#" lists the admin's order, a hidden
           snippet cannot be typed back into existence, and equal scores tie-break on that order
           rather than on registration order the rep never sees. */
        var rows = _arrange(SNIPPETS.map(function (s) {
          var row = _snippetRow(s, ctx);
          /* ⚠️ THE OLD NAME JOINS THE INVISIBLE HAYSTACK WHEN A ROW IS RENAMED. A rep with the
             previous name in muscle memory still finds it, and it lands at the `keywords` rank
             rather than the label rank — which is right, because the "#" picker highlights the
             query inside the LABEL, and a top-ranked hit with nothing highlighted reads as a bug. */
          row._keys = String(s.keywords || '');
          row._body = String(s.text || '');
          row._was = String(s.label || '');
          return row;
        }), 'snippet', false);

        if (!query) return rows.map(_stripSearchFields);

        var scored = [];
        rows.forEach(function (row, i) {
          /* ⛔ MATCHED ON THE LABEL THE REP CAN ACTUALLY SEE, which after a rename is the stored
             one. Ranking the built-in name would return rows the rep cannot recognise. */
          var label = String(row.label || '').toLowerCase();
          var keys = (row._keys + ' ' + (row._was === row.label ? '' : row._was)).toLowerCase();
          var body = row._body.toLowerCase();
          var rank = -1;
          if (label.indexOf(query) === 0) rank = 0;               // types the name from the start
          else if (label.indexOf(query) !== -1) rank = 1;
          else if (keys.indexOf(query) !== -1) rank = 2;
          else if (body.indexOf(query) !== -1) rank = 3;          // remembers a phrase, not the name
          if (rank >= 0) scored.push({ row: row, rank: rank, i: i });
        });
        scored.sort(function (a, b) { return a.rank - b.rank || a.i - b.i; });
        return scored.map(function (x) { return _stripSearchFields(x.row); });
      });
    },

    /* ============================================
       CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13) — A SNIPPET WITH A SECOND STEP
       ============================================
       Some snippets cannot be built from the deal alone: "link them to the installation
       instructions" needs to know WHICH manual, and on a deal with no ECORE line there is nothing
       to infer it from. Such a snippet declares `options(ctx)`; the modal opens a small searchable
       picker, and the chosen row comes back as `ctx.option` on the SAME build.

       ⛔ `hasOptions` IS SYNCHRONOUS, and deliberately: it is a property of the CODE registration,
       never of data, so the ⚡ menu can decide "does this one need a second step" without sitting
       on "Inserting…" while a fetch makes its mind up.

       ⚠️ `options()` IS ALLOWED TO BE SLOW and allowed to reject — it is the async half, judged
       after the rep has already chosen the row. A rejection surfaces as the picker's own error
       state with an unconditional way out; it must never be reported as an empty list, because
       "could not load" and "there are none" send a rep to different places.

       ⛔ THE ROW IS A WHOLE OBJECT, NOT A KEY. `build` receives what the rep actually picked, so a
       list refreshed between the pick and the build cannot re-resolve to a different document. */
    hasOptions: function (id, ctx) {
      var s = SNIPPETS.find(function (x) { return x.id === id; });
      if (!(s && typeof s.options === 'function')) return false;
      /* CUSTOM_SHIPMENTSPEC_BY_CLAUDE (LOGAN 5ea246b1adfb) - `optionsWhen(ctx)`, SYNC like the rest of this: a snippet
         whose second step only exists sometimes (the freight details: a picker only with two or more quotes attached).
         Absent = always, which is every snippet before it. A throw reads as "no picker", never a broken menu. */
      if (typeof s.optionsWhen === 'function') {
        try { return !!s.optionsWhen(ctx || {}); } catch (e) { return false; }
      }
      return true;
    },
    /* The second step's search box wording; the ECORE manuals' wording when a snippet names none. */
    optionsPlaceholder: function (id) {
      var s = SNIPPETS.find(function (x) { return x.id === id; });
      return (s && s.optionsPlaceholder) || '';
    },

    options: function (id, ctx) {
      var s = SNIPPETS.find(function (x) { return x.id === id; });
      if (!s) return Promise.reject(new Error('Unknown snippet: ' + id));
      if (typeof s.options !== 'function') return Promise.resolve(null);
      try {
        return Promise.resolve(s.options(ctx || {}));
      } catch (e) { return Promise.reject(e); }
    },

    build: function (id, ctx) {
      var s = SNIPPETS.find(function (x) { return x.id === id; });
      if (!s) return Promise.reject(new Error('Unknown snippet: ' + id));
      var c = ctx || {};
      /* ⛔ try/catch, not a bare Promise.resolve(build(c)). A snippet REFUSES by throwing
         ("attach the quote first"), and most of them do it synchronously before any read.
         A synchronous throw would escape past the caller's .catch and take the keystroke
         handler with it, so the picker would just stop responding instead of explaining. */
      /* CUSTOM_HUMANCOPYEDIT_BY_CLAUDE — drained here too. No snippet reaches a declared phrase
         today (the blocks they use are not wrapped yet), but the day one does, a fallback that
         reported to nobody would be exactly the silent failure the whole mechanism exists to
         avoid. Four lines now beats finding it later. */
      _drainPhraseWarnings();
      if (typeof s.build === 'function') {
        try {
          return Promise.resolve(s.build(c)).then(function (out) {
            var extra = _drainPhraseWarnings();
            if (extra.length) { out = out || {}; out.warnings = (out.warnings || []).concat(extra); }
            return out;
          });
        } catch (e) { _drainPhraseWarnings(); return Promise.reject(e); }
      }
      /* The `text` case. Tokens are resolved even when the string carries none - the reads
         are memoised per ref, so a snippet with no tokens costs nothing after the first. */
      return _tokenValues(c).then(function (values) {
        var r = _renderTokens(s.text, values);
        return { paragraphs: [r.runs], warnings: r.warnings };
      });
    },

    _internals: window.RQDTpl,
  };

  /* CUSTOM_HUMANTPLCATALOG_BY_CLAUDE — start fetching the arrangement, so the sync `list` getter
     has it before anything opens a menu.
     ⚠️ `setTimeout(…, 0)`, NOT A BARE CALL. Every file in this system is `defer`red, and on some
     pages the shared portal script — which defines RepPortal.API — parses AFTER this one. A call
     at parse time would find no API, resolve to "unknown" and (correctly) not memoise, so the
     work would simply be wasted; a timeout runs once all deferred scripts have executed.
     ⚠️ And it is best-effort by design: `_loadMeta` swallows its own failures, and every picker
     awaits it again anyway. Nothing here can stop a template from being inserted. */
  setTimeout(function () { _loadMeta(); }, 0);
})();
